/**
 * Lexical AST Editor
 * Modern editor implementation using Lexical with AST backend
 * Uses Lexical with AST backend for content representation
 */

import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { Close } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Popover, useMediaQuery, useTheme } from '@mui/material'
import type { EmbedReference } from '@shared/ast/astTypes'
import { inlineEmbedContent, isInlineEmbedReference } from '@shared/ast/embedReference'
import { findEnclosingChapterSectionHeadingBlockId } from '@shared/ast/extractChapterSection'
import { MOMENT_TAG_ID } from '@shared/tags/canonicalMomentTag'
import {
  $createRangeSelection,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  EditorState,
  LexicalEditor as LexicalEditorType,
  PASTE_COMMAND,
  PointType,
  REDO_COMMAND,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'

import {
  $embedDestinationContent,
  COPY_EMBED_COMMAND,
  EMBED_CLIPBOARD_PREFIX,
  PASTE_EMBED_COMMAND,
  canInsertInlineEmbed,
  parseEmbedClipboard,
  selectionTouchesProtectedContent,
  serializeEmbedClipboard,
} from './embedClipboard'
import { $captureEmbedSelection, type EmbedSelectionSnapshot } from './embedSelection'
import type { InlineProposalReview } from './InlineProposal'
import LexicalToolbar, {
  ADD_DEP_AFTER_COMMAND,
  ADD_DEP_BEFORE_COMMAND,
  ASK_ASSISTANT_COMMAND,
  TAG_SELECTION_COMMAND,
  TOGGLE_MOMENT_COMMAND,
} from './LexicalToolbar'
import {
  $createCustomEmbedNode,
  $createCustomInlineEmbedNode,
  $createCustomParagraphNode,
  $selectionContainsEmbed,
  CUSTOM_NODES,
} from './nodes'
import { EmbedContext } from './SourceEmbedView'
import { DebugPanel } from '../DebugPanel'
import { ClipboardPlugin } from './plugins/ClipboardPlugin'
import { DirectionPlugin } from './plugins/DirectionPlugin'
import { EncryptedContentPlugin } from './plugins/EncryptedContentPlugin'
import { InlineAnnotationPlugin } from './plugins/InlineAnnotationPlugin'
import { InlineEmbedNavigationPlugin } from './plugins/InlineEmbedNavigationPlugin'
import { InlineProposalsPlugin } from './plugins/InlineProposalsPlugin'
import { KeyboardShortcutsPlugin } from './plugins/KeyboardShortcutsPlugin'
import { ListEditingPlugin } from './plugins/ListEditingPlugin'
import { ListNodeReplacementPlugin } from './plugins/ListNodeReplacementPlugin'
import { NodeReplacementPlugin } from './plugins/NodeReplacementPlugin'
import { SelectionContextMenuPlugin } from './plugins/SelectionContextMenuPlugin'
import { SmoothCursorScrollPlugin } from './plugins/SmoothCursorScrollPlugin'
import { TagIndicatorPlugin } from './plugins/TagIndicatorPlugin'
import { WritingSyntaxPlugin } from './plugins/WritingSyntaxPlugin'
import apiService from '../../../services/api'
import { PAPER_BACKGROUND, PAPER_TEXTURE_OVERLAY } from '../../../styles/paperBackground'
import {
  AstEditorCommands,
  AstSelection,
  AstUndoRedoManager,
  Block,
  ContentAST,
  ListItem,
  astToLexical,
  createEditorCommands,
  createEmptyAST,
  createSelection,
  createUndoRedoManager,
  findAnyById,
  generateBlockId,
  getBlockText,
  getInlineText,
  lexicalToAst,
} from '../../../transforms/ast'
import { Annotation, SelectionContext, isInlineRangeAnchor } from '../../../types/annotations'
import {
  createAnnotationFromSelection,
  createBlockAnnotation,
  getAnnotationsForBlock,
  repairAnnotations,
  validateAnnotations,
} from '../../../utils/annotationUtils'
import { logger } from '../../../utils/logger'
import TagManagerDialog from '../../tags/TagManagerDialog'
import { type TagDefinition, getTagDisplayName, resolveTagColor } from '../../tags/tagTreeUtils'

type TagSelectionSnapshot = {
  ast: ContentAST
  selection: SelectionContext | null
  listItemIds?: string[]
}

export interface LexicalEditorProps {
  proposalReview?: InlineProposalReview
  initialContent?: ContentAST
  onChange?: (ast: ContentAST) => void
  onSelectionChange?: (selection: any) => void
  placeholder?: string
  readOnly?: boolean
  /** Use reader-optimized typography (larger font, looser line-height). Defaults to false. */
  readerView?: boolean
  className?: string
  style?: React.CSSProperties
  showToolbar?: boolean
  editorCommands?: AstEditorCommands
  undoRedoManager?: AstUndoRedoManager
  onSave?: (ast: ContentAST) => void | Promise<void>
  saving?: boolean
  autoSaving?: boolean
  hasUnsavedChanges?: boolean
  recentlySaved?: boolean
  showSaveButton?: boolean
  currentDirection?: 'ltr' | 'rtl'
  onToggleDirection?: () => void
  onSetDirection?: (direction: 'ltr' | 'rtl') => void
  debugMode?: boolean
  onToggleDebug?: () => void
  showLineNumbers?: boolean
  onToggleLineNumbers?: () => void
  onEncryptedClick?: (encryptedText: string, encryptionId: string) => void
  onEncryptedContextMenu?: (
    event: React.MouseEvent,
    encryptionId: string,
    kind?: { character?: string; authorHeld?: true },
  ) => void
  mnemonicMap?: Record<string, string>
  glyphMap?: Record<string, string>
  /** Encrypted nodes no key opens — a character's, or author-held. They are inert. */
  sealedIds?: Set<string>
  onCharacterConsent?: (encryptionId: string, anchor: HTMLElement) => void
  isAuthor?: boolean
  bookId?: string
  chapterId?: string
  /** Host navigation saves the outgoing file before opening the source. */
  onOpenEmbedSource?: (filename: string, blockId: string) => void
  /** Annotations for the chapter (new flexible system) */
  annotations?: Annotation[]
  /** Callback when an annotation is added or updated */
  onAnnotationChange?: (annotation: Annotation) => void
  /** Ask the authoring assistant about the current selection. */
  onAskAssistant?: (selection: { markedText?: string; startBlockId?: string }) => void
  /** Callback when the full annotation list should be replaced */
  onAnnotationsBulkUpdate?: (annotations: Annotation[]) => Promise<void> | void
  /** Callback when annotations are repaired after content changes */
  onAnnotationsRepaired?: (annotations: Annotation[], meta?: { expired: Annotation[] }) => Promise<void> | void
  toolbarContainerRef?: React.RefObject<HTMLDivElement>
  /** Book-level settings for default font family and size */
  bookSettings?: {
    defaultFontFamily?: string
    defaultFontSize?: string
    titleFontFamily?: string
  }
  /** Tag id → color map for colored tag pills */
  tagColorMap?: Record<string, string>
  /** Book-level tag registry for resolving ids into names/paths */
  tagRegistry?: Record<string, TagDefinition>
  /** Sync the local tag registry after creating tags in the selection dialog */
  onTagRegistrySaved?: (tags: Record<string, TagDefinition>) => void
  /** Block ID to scroll to on mount (e.g. from search navigation) */
  initialScrollBlockId?: string | null
  /** Text to highlight within the block on initial scroll */
  initialScrollHighlightText?: string | null
  /** Absolute text offset of the initial highlight within the block */
  initialScrollHighlightOffset?: number | null
  /** Exact scroll offset to restore on mount (returning to an already-visited tab). */
  initialScrollTop?: number | null
  /**
   * Caret to restore on mount, and the signal that this mount is a return to a
   * file the author was already editing — so the editor takes focus rather than
   * leaving them to click before they can type.
   */
  initialSelection?: AstSelection | null
  /**
   * Called from the selection context menu when the user picks "must come before…" or
   * "must come after…". The host (BookEditor) opens the AddDependencyDialog using these args.
   */
  onAddDependency?: (args: {
    direction: 'before' | 'after'
    /** Heading blockId of the ChapterSection containing the cursor; null if cursor is above any `^` heading. */
    currentSectionHeadingBlockId: string | null
    /** The text the user selected when invoking the context menu. */
    selectedText: string
  }) => void
}

export interface LexicalEditorRef {
  /** Sync debounced Lexical→React state immediately; returns AST if there was pending content, else null */
  flushPendingSyncToParent: () => ContentAST | null
  getAST: () => ContentAST
  setAST: (ast: ContentAST) => void
  refreshFromAST: () => void
  save: () => void
  undo: () => void
  redo: () => void
  focus: () => void
  blur: () => void
  validate: () => Promise<boolean>
  getEditorCommands: () => AstEditorCommands
  getUndoRedoManager: () => AstUndoRedoManager
  getEditor: () => LexicalEditorType | null
  triggerASTChange: (newAST: ContentAST) => void
  scrollToBlock: (blockId: string, highlightText?: string, highlightOffset?: number) => boolean
  // Direction control methods
  setBlockDirection: (direction: 'ltr' | 'rtl') => void
  toggleBlockDirection: () => void
  getCurrentBlockDirection: () => 'ltr' | 'rtl'
}

/**
 * Highlight a specific text match inside a block element using the CSS Custom Highlight API.
 * Adds a yellow background to the matched text without modifying the DOM.
 */
const blockHighlightCleanupMap = new WeakMap<Element, () => void>()
let searchResultHighlightTimeout: ReturnType<typeof setTimeout> | null = null
let searchResultHighlightAnimationFrame: number | null = null

const parseCssColor = (value: string): [number, number, number, number] | null => {
  const normalized = value.trim()

  const hexMatch = normalized.match(/^#([0-9a-f]{6}|[0-9a-f]{8})$/i)
  if (hexMatch) {
    const hex = hexMatch[1]
    const hasAlpha = hex.length === 8
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    const a = hasAlpha ? parseInt(hex.slice(6, 8), 16) / 255 : 1
    return [r, g, b, a]
  }

  const rgbMatch = normalized.match(/^rgba?\(([^)]+)\)$/i)
  if (rgbMatch) {
    const [r, g, b, a = '1'] = rgbMatch[1].split(',').map((part) => part.trim())
    return [Number(r), Number(g), Number(b), Number(a)]
  }

  return null
}

const animateSearchHighlightFade = () => {
  const root = document.documentElement
  const computedColor = getComputedStyle(root).getPropertyValue('--search-highlight')
  const parsedColor = parseCssColor(computedColor)

  if (!parsedColor) {
    root.style.removeProperty('--search-highlight-live')
    return
  }

  const [r, g, b, a] = parsedColor
  const durationMs = 2400
  const startedAt = performance.now()

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / durationMs)
    const eased = 1 - (1 - progress) * (1 - progress)
    const nextAlpha = a * (1 - eased)

    root.style.setProperty('--search-highlight-live', `rgba(${r}, ${g}, ${b}, ${nextAlpha.toFixed(3)})`)

    if (progress < 1) {
      searchResultHighlightAnimationFrame = window.requestAnimationFrame(tick)
      return
    }

    root.style.removeProperty('--search-highlight-live')
    searchResultHighlightAnimationFrame = null
  }

  if (searchResultHighlightAnimationFrame) {
    window.cancelAnimationFrame(searchResultHighlightAnimationFrame)
  }

  root.style.setProperty('--search-highlight-live', `rgba(${r}, ${g}, ${b}, ${a})`)
  searchResultHighlightAnimationFrame = window.requestAnimationFrame(tick)
}

const flashBlockHighlight = (blockEl: Element) => {
  blockHighlightCleanupMap.get(blockEl)?.()
  blockEl.classList.remove('search-highlight')
  // Force reflow so repeated jumps restart the CSS animation immediately.
  void (blockEl as HTMLElement).getBoundingClientRect()
  blockEl.classList.add('search-highlight')
  const cleanup = () => {
    blockEl.classList.remove('search-highlight')
    blockHighlightCleanupMap.delete(blockEl)
  }
  blockHighlightCleanupMap.set(blockEl, cleanup)
  blockEl.addEventListener('animationend', cleanup, { once: true })
}

// Block segments emit a virtual "\n" for every offset boundary the server inserts
// when computing blockMatchStart: <br> elements (AST TextNode("\n") rendered by the
// Lexical bridge) and the gap between sibling list items (server joins items with \n).
// Without these the DOM text walker is shorter than server's getBlockText and exact
// offsets miss, causing the indexOf fallback to land on the first match in the block.
type BlockTextSegment = { kind: 'text'; node: Text; start: number; end: number; text: string }
type BlockNewlineSegment = { kind: 'newline'; start: number; end: number }
type BlockSegment = BlockTextSegment | BlockNewlineSegment

const getBlockSegments = (blockEl: Element): BlockSegment[] => {
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n: Node) => {
      if (n.nodeType === Node.TEXT_NODE) {
        return NodeFilter.FILTER_ACCEPT
      }
      if (n.nodeType === Node.ELEMENT_NODE) {
        const tag = (n as Element).tagName
        if (tag === 'BR' || tag === 'LI') {
          return NodeFilter.FILTER_ACCEPT
        }
      }
      return NodeFilter.FILTER_SKIP
    },
  })
  const segments: BlockSegment[] = []
  let offset = 0
  let n: Node | null
  let firstListItemSeen = false
  while ((n = walker.nextNode())) {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n as Text
      const text = t.textContent ?? ''
      segments.push({ kind: 'text', node: t, start: offset, end: offset + text.length, text })
      offset += text.length
      continue
    }
    const tag = (n as Element).tagName
    if (tag === 'LI') {
      if (firstListItemSeen) {
        segments.push({ kind: 'newline', start: offset, end: offset + 1 })
        offset += 1
      }
      firstListItemSeen = true
      continue
    }
    // BR
    segments.push({ kind: 'newline', start: offset, end: offset + 1 })
    offset += 1
  }
  return segments
}

const locateBlockPosition = (
  segments: BlockSegment[],
  absoluteOffset: number,
): { node: Text; offset: number } | null => {
  if (segments.length === 0) {
    return null
  }

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (absoluteOffset > seg.end) {
      continue
    }
    if (seg.kind === 'text') {
      const localOffset = Math.max(0, Math.min(seg.text.length, absoluteOffset - seg.start))
      return { node: seg.node, offset: localOffset }
    }
    // Position falls on a virtual newline: anchor at the start of the next text
    // segment, or fall back to the end of the previous one.
    for (let j = i + 1; j < segments.length; j++) {
      const next = segments[j]
      if (next.kind === 'text') {
        return { node: next.node, offset: 0 }
      }
    }
    for (let j = i - 1; j >= 0; j--) {
      const prev = segments[j]
      if (prev.kind === 'text') {
        return { node: prev.node, offset: prev.text.length }
      }
    }
    return null
  }

  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i]
    if (seg.kind === 'text') {
      return { node: seg.node, offset: seg.text.length }
    }
  }
  return null
}

const findTextRangeInBlock = (blockEl: Element, text: string, exactOffset?: number | null): Range | null => {
  const segments = getBlockSegments(blockEl)
  if (segments.length === 0) {
    return null
  }

  const blockText = segments.map((seg) => (seg.kind === 'text' ? seg.text : '\n')).join('')
  const lowerBlockText = blockText.toLowerCase()
  const lowerText = text.toLowerCase()

  let startOffset = exactOffset ?? -1
  const exactMatch = startOffset >= 0 && lowerBlockText.slice(startOffset, startOffset + text.length) === lowerText

  if (!exactMatch) {
    startOffset = lowerBlockText.indexOf(lowerText)
  }

  if (startOffset < 0) {
    return null
  }

  const endOffset = startOffset + text.length
  const start = locateBlockPosition(segments, startOffset)
  const end = locateBlockPosition(segments, endOffset)

  if (!start || !end) {
    return null
  }

  const range = document.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  return range
}

const findScrollableAncestor = (element: Element) => {
  let current = element.parentElement
  while (current) {
    const style = getComputedStyle(current)
    const overflowY = style.overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

const scrollRangeIntoView = (blockEl: Element, range: Range) => {
  const rangeRect = range.getBoundingClientRect()
  if (rangeRect.height === 0 && rangeRect.width === 0) {
    ;(blockEl as HTMLElement).scrollIntoView({ block: 'center' })
    return
  }

  const scrollParent = findScrollableAncestor(blockEl)
  if (!scrollParent) {
    ;(blockEl as HTMLElement).scrollIntoView({ block: 'center' })
    return
  }

  const parentRect = scrollParent.getBoundingClientRect()
  const targetTop = parentRect.top + parentRect.height * 0.35
  const nextScrollTop = scrollParent.scrollTop + (rangeRect.top - targetTop)
  scrollParent.scrollTo({ top: nextScrollTop, behavior: 'auto' })
}

const highlightTextInBlock = (blockEl: Element, text: string, exactOffset?: number | null): Range | null => {
  const range = findTextRangeInBlock(blockEl, text, exactOffset)
  if (!range) {
    flashBlockHighlight(blockEl)
    return null
  }

  const cssHighlights = (CSS as any).highlights as Map<string, any> | undefined
  if (!cssHighlights) {
    flashBlockHighlight(blockEl)
    return range
  }

  const highlight = new (window as any).Highlight(range)
  cssHighlights.delete('search-result')
  cssHighlights.set('search-result', highlight)

  if (searchResultHighlightTimeout) {
    clearTimeout(searchResultHighlightTimeout)
  }

  animateSearchHighlightFade()

  searchResultHighlightTimeout = setTimeout(() => {
    cssHighlights.delete('search-result')
    document.documentElement.style.removeProperty('--search-highlight-live')
    searchResultHighlightTimeout = null
    if (searchResultHighlightAnimationFrame) {
      window.cancelAnimationFrame(searchResultHighlightAnimationFrame)
      searchResultHighlightAnimationFrame = null
    }
  }, 2400)
  return range
}

const scrollToBlockMatchNow = (blockEl: Element, highlightText?: string | null, highlightOffset?: number | null) => {
  if (highlightText) {
    const range = highlightTextInBlock(blockEl, highlightText, highlightOffset)
    if (range) {
      scrollRangeIntoView(blockEl, range)
      return
    }
  }

  ;(blockEl as HTMLElement).scrollIntoView({ block: 'center' })
  if (!highlightText) {
    flashBlockHighlight(blockEl)
  }
}

let pendingFontScrollCleanup: (() => void) | null = null

const scrollToBlockMatch = (blockEl: Element, highlightText?: string | null, highlightOffset?: number | null) => {
  pendingFontScrollCleanup?.()
  scrollToBlockMatchNow(blockEl, highlightText, highlightOffset)

  // A cold-load font swap can move a sentence by hundreds of pixels. Correct
  // once fonts settle, unless a newer target or the author's input supersedes it.
  if (document.fonts.status !== 'loading') {
    return
  }
  let cancelled = false
  const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const
  const cleanup = () => {
    cancelled = true
    for (const event of inputEvents) {
      window.removeEventListener(event, cleanup, true)
    }
    if (pendingFontScrollCleanup === cleanup) {
      pendingFontScrollCleanup = null
    }
  }
  pendingFontScrollCleanup = cleanup
  for (const event of inputEvents) {
    window.addEventListener(event, cleanup, { capture: true, passive: true })
  }
  void document.fonts.ready.then(() => {
    const shouldScroll = !cancelled && blockEl.isConnected
    cleanup()
    if (shouldScroll) {
      scrollToBlockMatchNow(blockEl, highlightText, highlightOffset)
    }
  })
}

const scrollToPassage = (
  container: Element,
  blockId: string,
  text?: string | null,
  offset?: number | null,
): boolean => {
  const block = container.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`)
  if (block) {
    scrollToBlockMatch(block, text, offset)
    return true
  }
  if (!text?.trim()) {
    return false
  }

  // A source edit can replace a block's ID. Recover only when the literal quote
  // identifies one occurrence in the current file; never guess between matches.
  let match: Element | null = null
  let matchOffset = 0
  for (const candidate of container.querySelectorAll('[data-block-id]')) {
    const candidateText = getBlockSegments(candidate)
      .map((segment) => (segment.kind === 'text' ? segment.text : '\n'))
      .join('')
    const start = candidateText.indexOf(text)
    if (start < 0) {
      continue
    }
    if (candidateText.indexOf(text, start + 1) >= 0) {
      return false
    }
    // A list and its item can expose the same occurrence. Keep the innermost
    // block, without treating the nested wrapper as a second match.
    if (match && !match.contains(candidate) && !candidate.contains(match)) {
      return false
    }
    if (!match || match.contains(candidate)) {
      match = candidate
      matchOffset = start
    }
  }
  if (!match) {
    return false
  }
  // An offset from the missing block has no meaning in its replacement.
  scrollToBlockMatch(match, text, matchOffset)
  return true
}

const findScrollParent = (el: HTMLElement): HTMLElement | null => {
  let parent = el.parentElement
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

/**
 * Inner editor component that uses the Lexical context
 */
const LexicalEditorInner: React.FC<{
  proposalReview?: InlineProposalReview
  ast: ContentAST
  setAST: React.Dispatch<React.SetStateAction<ContentAST>>
  onChange?: (ast: ContentAST) => void
  onSelectionChange?: (selection: any) => void
  placeholder?: string
  readOnly?: boolean
  readerView?: boolean
  showToolbar?: boolean
  activeEditorCommands: AstEditorCommands
  activeUndoRedoManager: AstUndoRedoManager
  onSave?: (ast: ContentAST) => void | Promise<void>
  saving?: boolean
  autoSaving?: boolean
  hasUnsavedChanges?: boolean
  recentlySaved?: boolean
  showSaveButton?: boolean
  currentDirection?: 'ltr' | 'rtl'
  onToggleDirection?: () => void
  onSetDirection?: (direction: 'ltr' | 'rtl') => void
  debugMode?: boolean
  onToggleDebug?: () => void
  showLineNumbers?: boolean
  onToggleLineNumbers?: () => void
  onEncryptedClick?: (encryptedText: string, encryptionId: string) => void
  onEncryptedContextMenu?: (
    event: React.MouseEvent,
    encryptionId: string,
    kind?: { character?: string; authorHeld?: true },
  ) => void
  mnemonicMap?: Record<string, string>
  glyphMap?: Record<string, string>
  /** Encrypted nodes that belong to a person; no key opens them, so they are inert. */
  sealedIds?: Set<string>
  onCharacterConsent?: (encryptionId: string, anchor: HTMLElement) => void
  isAuthor?: boolean
  bookId?: string
  chapterId?: string
  annotations?: Annotation[]
  onAnnotationChange?: (annotation: Annotation) => void
  onAnnotationsBulkUpdate?: (annotations: Annotation[]) => Promise<void> | void
  onAskAssistant?: (selection: { markedText?: string; startBlockId?: string }) => void
  onAnnotationsRepaired?: (annotations: Annotation[], meta?: { expired: Annotation[] }) => Promise<void> | void
  toolbarContainerRef?: React.RefObject<HTMLDivElement>
  editorRef: React.MutableRefObject<LexicalEditorRef | null>
  bookSettings?: {
    defaultFontFamily?: string
    defaultFontSize?: string
    titleFontFamily?: string
  }
  tagColorMap?: Record<string, string>
  tagRegistry?: Record<string, TagDefinition>
  onTagRegistrySaved?: (tags: Record<string, TagDefinition>) => void
  initialScrollBlockId?: string | null
  initialScrollHighlightText?: string | null
  initialScrollHighlightOffset?: number | null
  initialScrollTop?: number | null
  initialSelection?: AstSelection | null
  onAddDependency?: (args: {
    direction: 'before' | 'after'
    currentSectionHeadingBlockId: string | null
    selectedText: string
  }) => void
}> = ({
  ast,
  setAST,
  proposalReview,
  onChange,
  onSelectionChange,
  placeholder = 'Start writing...',
  readOnly = false,
  readerView = false,
  showToolbar = false,
  activeEditorCommands,
  activeUndoRedoManager,
  onSave,
  saving,
  autoSaving,
  hasUnsavedChanges,
  recentlySaved,
  showSaveButton = true,
  currentDirection = 'ltr',
  onToggleDirection,
  onSetDirection,
  debugMode = false,
  onToggleDebug,
  showLineNumbers = false,
  onToggleLineNumbers,
  onEncryptedClick,
  onEncryptedContextMenu,
  mnemonicMap = {},
  glyphMap = {},
  sealedIds,
  onCharacterConsent,
  isAuthor = false,
  bookId,
  chapterId,
  annotations = [],
  onAnnotationChange,
  onAnnotationsBulkUpdate,
  onAskAssistant,
  onAnnotationsRepaired,
  toolbarContainerRef,
  editorRef,
  bookSettings,
  tagColorMap = {},
  tagRegistry = {},
  onTagRegistrySaved,
  initialScrollBlockId,
  initialScrollHighlightText,
  initialScrollHighlightOffset,
  initialScrollTop,
  initialSelection,
  onAddDependency,
}) => {
  const [editor] = useLexicalComposerContext()
  const HOVER_OVERLAY_CLEAR_DELAY_MS = 420
  const [showWritingSyntax, setShowWritingSyntax] = useState(
    () => localStorage.getItem('editor:show-writing-syntax') === 'true',
  )

  // On mobile (xs) the editor's left/right padding is only 12–24px and is
  // easily hit by accident when trying to start a text selection — and
  // editor.focus() then jumps the viewport back to the existing selection.
  // Restrict the paper-margin-tap-to-focus convenience to sm and up.
  const theme = useTheme()
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('sm'))

  // Keep Lexical editability in sync with lock state after mount.
  useEffect(() => {
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  // Expose editor instance and AST utilities for E2E testing (development only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      ;(window as any).__lexicalEditor = editor
      // Expose AST bridge functions for E2E tests
      import('../../../transforms/ast/lexicalBridge').then((bridge) => {
        ;(window as any).__astBridge = {
          lexicalToAst: bridge.lexicalToAst,
          astToLexical: bridge.astToLexical,
        }
      })
      import('../../../utils/astValidation').then((validation) => {
        ;(window as any).__astValidation = {
          compareAST: validation.compareAST,
          formatDiffSummary: validation.formatDiffSummary,
        }
      })
    }
    return () => {
      if (import.meta.env.DEV) {
        delete (window as any).__lexicalEditor
        delete (window as any).__astBridge
        delete (window as any).__astValidation
      }
    }
  }, [editor])

  // Track if auto-focus has been attempted (prevents multiple attempts from StrictMode)
  const autoFocusAttemptedRef = useRef(false)

  // Scroll to a specific block on mount (e.g. from search, heading navigation, or a
  // deep-link hash on a cold page load). On a cold load the block isn't in the DOM
  // yet when this runs — Lexical renders it a moment later — so rather than guessing
  // a delay, observe the editor and scroll exactly when the block element appears.
  useEffect(() => {
    if (!initialScrollBlockId) {
      return
    }
    const container = editorContainerRef.current
    if (!container) {
      return
    }

    const scrollIfPresent = (): boolean =>
      scrollToPassage(container, initialScrollBlockId, initialScrollHighlightText, initialScrollHighlightOffset)

    // Warm path: the block is already rendered (in-app navigation).
    if (scrollIfPresent()) {
      return
    }

    // Cold path: react to Lexical rendering the content. The initial render is a
    // single reconciliation, so once any block exists the target is either present
    // or absent for good — either way we stop observing (no timers, no leak).
    const observer = new MutationObserver(() => {
      if (scrollIfPresent() || container.querySelector('[data-block-id]')) {
        observer.disconnect()
      }
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
    }
  }, [initialScrollBlockId, initialScrollHighlightOffset, initialScrollHighlightText])

  // Restore an exact scroll offset on mount (returning to an already-visited tab).
  // Same wait-for-initial-render strategy as the block-scroll effect above.
  useEffect(() => {
    if (initialScrollTop == null || initialScrollBlockId) {
      return
    }
    const container = editorContainerRef.current
    if (!container) {
      return
    }

    const restoreIfRendered = (): boolean => {
      if (!container.querySelector('[data-block-id]')) {
        return false
      }
      const scroller = findScrollParent(container)
      if (scroller) {
        scroller.scrollTop = initialScrollTop
      }
      return true
    }

    if (restoreIfRendered()) {
      return
    }
    const observer = new MutationObserver(() => {
      if (restoreIfRendered()) {
        observer.disconnect()
      }
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
    }
  }, [initialScrollTop, initialScrollBlockId])

  // Auto-focus the editor on mount (skip when scrolling to a block)
  useEffect(() => {
    // Skip on touch devices: programmatic focus there marks the editor active WITHOUT raising the
    // keyboard, and a later tap on the already-focused field often fails to summon it (no focus
    // change). Desktop wants click-to-type; touch users tap when they're ready.
    const isTouch = typeof window !== 'undefined' && window.matchMedia('(hover: none) and (pointer: coarse)').matches
    // A remembered caret means this is a return to a file the author was already
    // editing. That overrides the scroll-target skip below, which exists only to
    // stop a caret at the document start from yanking the view back to the top —
    // the remembered caret sits where the view was restored to, so it can't.
    const returningToCaret = Boolean(initialSelection)
    const skipForScrollTarget = Boolean(initialScrollBlockId) || initialScrollTop != null
    if (!isTouch && !readOnly && !autoFocusAttemptedRef.current && (returningToCaret || !skipForScrollTarget)) {
      autoFocusAttemptedRef.current = true

      // Use a delay to ensure DOM is fully ready (paragraph node must be created first)
      setTimeout(() => {
        // Don't steal focus from something the author is actually using. On a
        // plain mount that means the body; on a tab switch the click landed on a
        // tab button, which is a request to go edit that file — so the test is
        // "is anything text-entry focused", not "is the body focused".
        const active = document.activeElement as HTMLElement | null
        const authorIsTypingElsewhere = Boolean(
          active && (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA'),
        )
        const focusIsFree = active === document.body || active?.tagName === 'BODY'
        if (returningToCaret ? !authorIsTypingElsewhere : focusIsFree) {
          const rootElement = editor.getRootElement()
          if (rootElement) {
            // Focus the DOM element directly. preventScroll is essential: the default focus()
            // scrolls the editor into view, nudging the page up a few px on mount — and since the
            // shell locks body scroll, that offset can't be scrolled back, trimming the header top.
            rootElement.focus({ preventScroll: true })

            // Then put the caret back where the author left it, or at the start
            editor.update(() => {
              if (initialSelection && restoreAstSelection(initialSelection)) {
                return
              }
              const root = $getRoot()
              const firstChild = root.getFirstChild()
              if (firstChild) {
                firstChild.selectStart()
              }
            })
          }
        }
      }, 300)
    }
  }, [])
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null)
  const [tagOverlayPosition, setTagOverlayPosition] = useState<{
    top: number
    left: number | null
    right: number | null
  } | null>(null)
  const blockOverlayClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearBlockOverlayTimeout = useCallback(() => {
    if (blockOverlayClearTimeoutRef.current) {
      clearTimeout(blockOverlayClearTimeoutRef.current)
      blockOverlayClearTimeoutRef.current = null
    }
  }, [])

  const scheduleBlockOverlayClear = useCallback(() => {
    clearBlockOverlayTimeout()
    blockOverlayClearTimeoutRef.current = setTimeout(() => {
      setHoveredBlockId(null)
      setTagOverlayPosition(null)
      blockOverlayClearTimeoutRef.current = null
    }, HOVER_OVERLAY_CLEAR_DELAY_MS)
  }, [clearBlockOverlayTimeout, HOVER_OVERLAY_CLEAR_DELAY_MS])

  // Callback for TagIndicatorPlugin to update hover state
  const handleBlockHover = useCallback(
    (blockId: string | null, position: { top: number; left: number | null; right: number | null } | null) => {
      if (blockId && position) {
        clearBlockOverlayTimeout()
        setHoveredBlockId(blockId)
        setTagOverlayPosition(position)
        return
      }
      scheduleBlockOverlayClear()
    },
    [clearBlockOverlayTimeout, scheduleBlockOverlayClear],
  )

  // State for inline annotation hover
  const [inlineHoverInfo, setInlineHoverInfo] = useState<{
    annotationId: string
    tags: string[]
    position: { top: number; left: number | null; right: number | null }
  } | null>(null)
  const inlineOverlayClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearInlineOverlayTimeout = useCallback(() => {
    if (inlineOverlayClearTimeoutRef.current) {
      clearTimeout(inlineOverlayClearTimeoutRef.current)
      inlineOverlayClearTimeoutRef.current = null
    }
  }, [])

  const scheduleInlineOverlayClear = useCallback(() => {
    clearInlineOverlayTimeout()
    inlineOverlayClearTimeoutRef.current = setTimeout(() => {
      setInlineHoverInfo(null)
      inlineOverlayClearTimeoutRef.current = null
    }, HOVER_OVERLAY_CLEAR_DELAY_MS)
  }, [clearInlineOverlayTimeout, HOVER_OVERLAY_CLEAR_DELAY_MS])

  // Callback for InlineAnnotationPlugin to update hover state
  const handleInlineHover = useCallback(
    (
      info: {
        annotationId: string
        tags: string[]
        position: { top: number; left: number | null; right: number | null }
      } | null,
    ) => {
      if (info) {
        clearInlineOverlayTimeout()
        setInlineHoverInfo({
          annotationId: info.annotationId,
          tags: info.tags,
          position: info.position,
        })
      } else {
        scheduleInlineOverlayClear()
      }
    },
    [clearInlineOverlayTimeout, scheduleInlineOverlayClear],
  )

  // On mobile the side-margin chip overlaps text (gutter is only 24px). Instead
  // we show a small dot in the gutter; tapping it pops the full chip list out
  // of the editor bounds via MUI Popover.
  const [blockPopoverAnchor, setBlockPopoverAnchor] = useState<HTMLElement | null>(null)
  const [inlinePopoverAnchor, setInlinePopoverAnchor] = useState<HTMLElement | null>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const [tagManagerSelectionOpen, setTagManagerSelectionOpen] = useState(false)
  const [pendingTagSelection, setPendingTagSelection] = useState<TagSelectionSnapshot | null>(null)

  // Track the current portal host element for toolbar rendering.
  const [portalContainerEl, setPortalContainerEl] = useState<HTMLDivElement | null>(null)

  // Keep portal host in sync with late-mounting toolbar containers.
  useEffect(() => {
    if (!toolbarContainerRef) {
      setPortalContainerEl(null)
      return
    }

    let rafId: number | null = null
    let cancelled = false

    const syncContainer = () => {
      if (cancelled) {
        return
      }

      const nextContainer = toolbarContainerRef.current
      setPortalContainerEl((prev) => (prev === nextContainer ? prev : nextContainer))

      // The toolbar host can appear one or more frames after mount.
      // Continue polling until it exists so the toolbar is never left unmounted.
      if (!nextContainer && showToolbar) {
        rafId = window.requestAnimationFrame(syncContainer)
      }
    }

    syncContainer()

    return () => {
      cancelled = true
      if (rafId != null) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [toolbarContainerRef, showToolbar])

  // PERFORMANCE FIX: Debounce React state sync to prevent re-render on every keystroke
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingASTRef = useRef<ContentAST | null>(null)
  const lastTagSelectionSnapshotRef = useRef<TagSelectionSnapshot | null>(null)
  const lastEmbedSelectionSnapshotRef = useRef<EmbedSelectionSnapshot | null>(null)

  const createTagSelectionSnapshot = useCallback(
    (editorState: EditorState) =>
      editorState.read<TagSelectionSnapshot | null>(() => {
        const liveAst = lexicalToAst(editorState.toJSON(), {
          lang: ast.meta.lang,
          dir: ast.meta.dir,
          title: ast.meta.title,
          defaultFontFamily: bookSettings?.defaultFontFamily,
          defaultFontSize: bookSettings?.defaultFontSize,
        })
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          return null
        }
        const wholeListItemIds = resolveWholeListItemSelection(selection, liveAst, editor.getRootElement())
        if (wholeListItemIds && wholeListItemIds.length > 0) {
          return {
            selection: null,
            ast: liveAst,
            listItemIds: wholeListItemIds,
          }
        }
        const selectionContext = createSelectionContextFromLexicalSelection(selection, liveAst, editor.getRootElement())
        if (!selectionContext) {
          return null
        }
        return {
          selection: selectionContext,
          ast: liveAst,
        }
      }),
    [ast.meta.dir, ast.meta.lang, ast.meta.title, bookSettings?.defaultFontFamily, bookSettings?.defaultFontSize],
  )

  // Handle editor state changes - debounced to prevent React re-render storm
  const handleEditorUpdate = useCallback(
    (
      editorState: EditorState,
      prevEditorState: EditorState,
      dirtyElements: Map<string, unknown>,
      dirtyLeaves: Set<string>,
    ) => {
      const isSelectionOnly = dirtyElements.size === 0 && dirtyLeaves.size === 0

      editorState.read(() => {
        // Read the post-change Lexical selection.
        // IMPORTANT: anchor.offset is within the text node, not the block.
        // Compute block-level offset by summing preceding text node sizes.
        const selection = $getSelection()
        let postChangeSelection = activeUndoRedoManager.getCurrentSelection()
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode()
          const blockId = findBlockIdFromNode(anchorNode)
          if (blockId) {
            const blockOffset = getBlockLevelOffset(anchorNode, selection.anchor.offset)
            postChangeSelection = createSelection(blockId, blockOffset)
          }
        }

        // Selection-only updates (clicks, arrow keys) must not start or extend an
        // undo batch — otherwise the next real edit's editorStateBefore captures
        // the pre-move cursor position and undo lands the cursor in the wrong place.
        // We still track the cursor on the manager so the next real edit's
        // pre-change selection is correct.
        if (isSelectionOnly) {
          activeUndoRedoManager.setCurrentSelection(postChangeSelection)
          return
        }

        const serializedState = editorState.toJSON()
        const newAST = lexicalToAst(serializedState, {
          lang: ast.meta.lang,
          dir: ast.meta.dir,
          title: ast.meta.title,
          defaultFontFamily: bookSettings?.defaultFontFamily,
          defaultFontSize: bookSettings?.defaultFontSize,
        })

        // Capture state change BEFORE updating selection so pendingSelection
        // records the pre-change cursor position for correct undo restoration.
        // prevEditorState is Lexical's authoritative pre-edit snapshot — it
        // reflects the cursor as it was immediately before this edit fired,
        // including any prior click-to-move.
        const preChangeSelection = activeUndoRedoManager.getCurrentSelection()
        activeUndoRedoManager.setEditorState(prevEditorState, editorState)
        activeUndoRedoManager.captureStateChange(newAST, preChangeSelection, 'typing')
        activeUndoRedoManager.setCurrentSelection(postChangeSelection)

        // Store pending AST - will be synced after debounce
        pendingASTRef.current = newAST

        // Clear existing debounce timer
        if (syncDebounceRef.current) {
          clearTimeout(syncDebounceRef.current)
        }

        // PERFORMANCE FIX: Debounce React state updates
        // Only sync state after 150ms of no typing - prevents React re-render storm
        syncDebounceRef.current = setTimeout(() => {
          const astToSync = pendingASTRef.current
          if (astToSync) {
            pendingASTRef.current = null
            setAST(astToSync)
            onChange?.(astToSync)
            onSelectionChange?.(activeUndoRedoManager.getCurrentSelection())
          }
        }, 150)
      })
    },
    [ast.meta, bookSettings, onChange, setAST, activeUndoRedoManager, onSelectionChange],
  )

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, prevEditorState, dirtyElements, dirtyLeaves, tags }) => {
      if (tags.has('history-merge')) {
        return
      }
      handleEditorUpdate(editorState, prevEditorState, dirtyElements, dirtyLeaves)
    })
  }, [editor, handleEditorUpdate])

  const getLiveAstFromEditor = useCallback((): ContentAST => {
    const editorState = editor.getEditorState()
    const serializedState = editorState.toJSON()
    return lexicalToAst(serializedState, {
      lang: ast.meta.lang,
      dir: ast.meta.dir,
      title: ast.meta.title,
      defaultFontFamily: bookSettings?.defaultFontFamily,
      defaultFontSize: bookSettings?.defaultFontSize,
    })
  }, [
    editor,
    ast.meta.dir,
    ast.meta.lang,
    ast.meta.title,
    bookSettings?.defaultFontFamily,
    bookSettings?.defaultFontSize,
  ])

  const flushPendingSyncToParent = useCallback((): ContentAST | null => {
    if (syncDebounceRef.current) {
      clearTimeout(syncDebounceRef.current)
      syncDebounceRef.current = null
    }
    const pending = pendingASTRef.current
    if (!pending) {
      return null
    }
    pendingASTRef.current = null
    setAST(pending)
    onChange?.(pending)
    onSelectionChange?.(activeUndoRedoManager.getCurrentSelection())
    return pending
  }, [onChange, onSelectionChange, setAST, activeUndoRedoManager])

  const createEmbedSelectionSnapshot = useCallback(
    () => editor.getEditorState().read(() => $captureEmbedSelection(getLiveAstFromEditor())),
    [editor, getLiveAstFromEditor],
  )

  // Handle save
  const handleSave = useCallback(() => {
    const flushed = flushPendingSyncToParent()
    onSave?.(flushed ?? getLiveAstFromEditor())
  }, [flushPendingSyncToParent, getLiveAstFromEditor, onSave])

  // Handle copy debug data
  const handleCopyDebugData = useCallback(async () => {
    try {
      const editorState = editor.getEditorState()
      const serializedState = editorState.toJSON()

      const debugData = [
        '='.repeat(80),
        'DEBUG DATA SNAPSHOT',
        '='.repeat(80),
        '',
        '--- AST (Abstract Syntax Tree) ---',
        JSON.stringify(ast, null, 2),
        '',
        '--- Lexical State ---',
        JSON.stringify(serializedState, null, 2),
        '',
        '='.repeat(80),
      ].join('\n')

      await navigator.clipboard.writeText(debugData)
    } catch (error) {
      logger.error('Failed to copy debug data:', error)
    }
  }, [ast, editor])

  const hoveredBlockTagItems = useMemo(() => {
    if (!hoveredBlockId || !annotations) {
      return []
    }

    const relevantAnnotations = getAnnotationsForBlock(annotations, hoveredBlockId, ast).filter(
      (annotation) => !isInlineRangeAnchor(annotation.anchor),
    )
    const tagToAnnotationIds = new Map<string, Set<string>>()

    relevantAnnotations.forEach((annotation) => {
      annotation.tags.forEach((tagId) => {
        const annotationIds = tagToAnnotationIds.get(tagId) || new Set<string>()
        annotationIds.add(annotation.id)
        tagToAnnotationIds.set(tagId, annotationIds)
      })
    })

    return Array.from(tagToAnnotationIds.entries()).map(([tagId, annotationIds]) => ({
      tagId,
      annotationIds: Array.from(annotationIds),
    }))
  }, [annotations, ast, hoveredBlockId])

  const hoveredInlineTagItems = useMemo(() => {
    if (!inlineHoverInfo) {
      return []
    }

    return inlineHoverInfo.tags.map((tagId) => ({
      tagId,
      annotationIds: [inlineHoverInfo.annotationId],
    }))
  }, [inlineHoverInfo])

  const [removingHoverTagKey, setRemovingHoverTagKey] = useState<string | null>(null)

  const handleRemoveHoverTag = useCallback(
    async (tagId: string, annotationIds: string[]) => {
      if (!annotations.length || annotationIds.length === 0 || !onAnnotationsBulkUpdate) {
        return
      }

      const targetIds = new Set(annotationIds)
      let didRemove = false
      const nextAnnotations = annotations.flatMap((annotation) => {
        if (!targetIds.has(annotation.id) || !annotation.tags.includes(tagId)) {
          return [annotation]
        }

        didRemove = true
        const nextTags = annotation.tags.filter((existingTagId) => existingTagId !== tagId)
        return nextTags.length > 0 ? [{ ...annotation, tags: nextTags }] : []
      })

      if (!didRemove) {
        return
      }

      const actionKey = `${tagId}:${annotationIds.slice().sort().join(',')}`
      setRemovingHoverTagKey(actionKey)
      try {
        await onAnnotationsBulkUpdate(nextAnnotations)
      } finally {
        setRemovingHoverTagKey((current) => (current === actionKey ? null : current))
      }
    },
    [annotations, onAnnotationsBulkUpdate],
  )

  // Single tag chip — shared by desktop margin overlays and the mobile dot's popover.
  const renderTagChip = ({ tagId, annotationIds }: { tagId: string; annotationIds: string[] }) => {
    const label = getTagDisplayName(tagRegistry, tagId)
    const c = resolveTagColor(tagId, tagColorMap, tagRegistry)
    const actionKey = `${tagId}:${annotationIds.slice().sort().join(',')}`
    const isRemoving = removingHoverTagKey === actionKey
    return (
      <Box
        key={actionKey}
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '1px',
          height: '18px',
          paddingLeft: '7px',
          paddingRight: '2px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          color: c,
          backgroundColor: `${c}22`,
          border: `1px solid ${c}66`,
          borderRadius: '9px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
      >
        {label}
        <IconButton
          size="small"
          disabled={isRemoving}
          aria-label={`Remove ${label}`}
          onMouseDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            handleRemoveHoverTag(tagId, annotationIds)
          }}
          sx={{
            color: c,
            p: 0,
            width: 14,
            height: 14,
            '&:hover': { backgroundColor: `${c}1a` },
            '&.Mui-disabled': {
              color: c,
              opacity: 0.65,
            },
          }}
        >
          {isRemoving ? <CircularProgress size={9} sx={{ color: c }} /> : <Close sx={{ fontSize: 11 }} />}
        </IconButton>
      </Box>
    )
  }

  useEffect(() => {
    return () => {
      clearBlockOverlayTimeout()
      clearInlineOverlayTimeout()
    }
  }, [clearBlockOverlayTimeout, clearInlineOverlayTimeout])

  // Validate and repair annotations when AST changes
  const lastAstRef = useRef<ContentAST | null>(null)
  useEffect(() => {
    if (!onAnnotationsRepaired || annotations.length === 0) {
      return
    }

    // Only run when AST actually changed (not on initial mount)
    if (lastAstRef.current === null) {
      lastAstRef.current = ast
      return
    }

    // Simple reference equality check - if same object, skip
    if (lastAstRef.current === ast) {
      return
    }
    lastAstRef.current = ast

    // Validate annotations against current AST
    const validated = validateAnnotations(annotations, ast)
    const needsRepair = validated.some((a) => a.status !== 'valid')
    const hasOrphanStamp = annotations.some((a) => a.orphanedAt)

    if (needsRepair || hasOrphanStamp) {
      const { repaired, expired, changed } = repairAnnotations(validated, ast)

      // Only notify if the persisted list actually differs
      if (changed) {
        onAnnotationsRepaired(repaired, { expired })

        if (expired.length > 0) {
          logger.warn(`${expired.length} annotations expired — anchor stayed dead past the re-attach window`, {
            expiredIds: expired.map((a) => a.id),
          })
        }
      }
    }
  }, [ast, annotations, onAnnotationsRepaired])

  const handleCloseTagManagerSelection = useCallback(() => {
    setTagManagerSelectionOpen(false)
    setPendingTagSelection(null)
  }, [])

  const handleSelectTagsFromManager = useCallback(
    (selectedTags: string[]) => {
      if (!pendingTagSelection || selectedTags.length === 0) {
        return
      }
      if (pendingTagSelection.listItemIds && pendingTagSelection.listItemIds.length > 0) {
        const annotationsToCreate = pendingTagSelection.listItemIds.map((itemId) =>
          createBlockAnnotation(itemId, selectedTags),
        )

        if (annotationsToCreate.length === 1) {
          onAnnotationChange?.(annotationsToCreate[0])
        } else if (onAnnotationsBulkUpdate) {
          onAnnotationsBulkUpdate([...annotations, ...annotationsToCreate])
        } else {
          annotationsToCreate.forEach((annotation) => onAnnotationChange?.(annotation))
        }

        handleCloseTagManagerSelection()
        return
      }

      if (!pendingTagSelection.selection) {
        return
      }

      const annotation = createAnnotationFromSelection(
        pendingTagSelection.selection,
        selectedTags,
        pendingTagSelection.ast,
      )
      onAnnotationChange?.(annotation)
      handleCloseTagManagerSelection()
    },
    [annotations, handleCloseTagManagerSelection, onAnnotationChange, onAnnotationsBulkUpdate, pendingTagSelection],
  )

  // Register command handler for tag selection (inline / block / span based on selection shape)
  useEffect(() => {
    if (!isAuthor || !onAnnotationChange) {
      return
    }

    return editor.registerCommand(
      TAG_SELECTION_COMMAND,
      () => {
        const liveSelectionSnapshot = createTagSelectionSnapshot(editor.getEditorState())
        const selectionSnapshot = liveSelectionSnapshot || lastTagSelectionSnapshotRef.current
        const selectionContext = selectionSnapshot?.selection
        const listItemIds = selectionSnapshot?.listItemIds ?? []

        if (!selectionContext && listItemIds.length === 0) {
          toast.error('Place cursor or select text to tag')
          return true
        }

        if (
          listItemIds.length === 0 &&
          selectionContext != null &&
          selectionContext.type === 'textRange' &&
          (!selectionContext.selectedText || selectionContext.selectedText.trim().length === 0)
        ) {
          toast.error('Cannot tag empty or whitespace-only selection')
          return true
        }
        if (!bookId) {
          toast.error('Cannot tag without book context')
          return true
        }
        setPendingTagSelection(selectionSnapshot)
        setTagManagerSelectionOpen(true)
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [bookId, createTagSelectionSnapshot, editor, isAuthor, onAnnotationChange])

  // Register ASK_ASSISTANT_COMMAND — hand the marked passage to the craft chat.
  // Reuses the tag-selection snapshot so "what did the author mark" has exactly one
  // definition in this file, whatever the selection shape.
  useEffect(() => {
    if (!isAuthor || !onAskAssistant) {
      return
    }

    return editor.registerCommand(
      ASK_ASSISTANT_COMMAND,
      () => {
        const snapshot = createTagSelectionSnapshot(editor.getEditorState())
        const selection = snapshot?.selection
        onAskAssistant({
          markedText: selection?.selectedText?.trim() || undefined,
          startBlockId: selection?.startBlockId,
        })
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [createTagSelectionSnapshot, editor, isAuthor, onAskAssistant])

  // Register TOGGLE_MOMENT_COMMAND handler — mark/unmark the "strong moment" tag on the selection.
  // Mark: create an annotation for the selection with [MOMENT_TAG_ID].
  // Unmark: any annotation covering a block in the selection with the moment tag has that tag stripped
  // (annotation dropped if moment was its only tag).
  useEffect(() => {
    if (!isAuthor) {
      return
    }

    return editor.registerCommand(
      TOGGLE_MOMENT_COMMAND,
      () => {
        const liveSelectionSnapshot = createTagSelectionSnapshot(editor.getEditorState())
        const selectionSnapshot = liveSelectionSnapshot || lastTagSelectionSnapshotRef.current
        const selectionContext = selectionSnapshot?.selection
        const listItemIds = selectionSnapshot?.listItemIds ?? []
        const snapshotAst = selectionSnapshot?.ast

        if ((!selectionContext && listItemIds.length === 0) || !snapshotAst) {
          toast.error('Select some text to mark a strong moment')
          return true
        }

        // Resolve the set of block / list-item ids the selection covers — used for both
        // detecting existing moment annotations and creating new ones.
        let coveredIds: string[]
        if (listItemIds.length > 0) {
          coveredIds = listItemIds
        } else {
          const startBlockId = selectionContext!.startBlockId
          const endBlockId = selectionContext!.endBlockId
          const startIndex = snapshotAst.blocks.findIndex((b) => b.id === startBlockId)
          const endIndex = snapshotAst.blocks.findIndex((b) => b.id === endBlockId)
          if (startIndex !== -1 && endIndex !== -1) {
            const lo = Math.min(startIndex, endIndex)
            const hi = Math.max(startIndex, endIndex)
            coveredIds = []
            for (let i = lo; i <= hi; i++) {
              coveredIds.push(snapshotAst.blocks[i].id)
            }
          } else {
            // One or both endpoints aren't top-level blocks — they're list items (or other
            // nested ids). Use endpoints as-is; we can't expand a range through a parent we
            // don't traverse here, and in practice in-list-item selections are confined to one item.
            coveredIds = startBlockId === endBlockId ? [startBlockId] : [startBlockId, endBlockId]
          }
        }

        const momentAnnotationIds = new Set<string>()
        for (const blockId of coveredIds) {
          const covering = getAnnotationsForBlock(annotations, blockId, snapshotAst)
          for (const ann of covering) {
            if (ann.tags.includes(MOMENT_TAG_ID)) {
              momentAnnotationIds.add(ann.id)
            }
          }
        }

        if (momentAnnotationIds.size > 0) {
          // Unmark: strip moment from each matching annotation; drop empties.
          if (!onAnnotationsBulkUpdate) {
            return true
          }
          const next = annotations.flatMap((annotation) => {
            if (!momentAnnotationIds.has(annotation.id)) {
              return [annotation]
            }
            const remaining = annotation.tags.filter((tagId) => tagId !== MOMENT_TAG_ID)
            return remaining.length > 0 ? [{ ...annotation, tags: remaining }] : []
          })
          void onAnnotationsBulkUpdate(next)
          return true
        }

        // Mark: list-item selections become one block annotation per item;
        // other selections use createAnnotationFromSelection's normal shaping.
        if (listItemIds.length > 0) {
          const annotationsToCreate = listItemIds.map((itemId) => createBlockAnnotation(itemId, [MOMENT_TAG_ID]))
          if (annotationsToCreate.length > 1 && onAnnotationsBulkUpdate) {
            void onAnnotationsBulkUpdate([...annotations, ...annotationsToCreate])
          } else if (onAnnotationChange) {
            annotationsToCreate.forEach((annotation) => onAnnotationChange(annotation))
          }
          return true
        }

        if (!onAnnotationChange) {
          return true
        }
        if (
          selectionContext!.type === 'textRange' &&
          (!selectionContext!.selectedText || selectionContext!.selectedText.trim().length === 0)
        ) {
          toast.error('Cannot mark an empty selection')
          return true
        }
        const annotation = createAnnotationFromSelection(selectionContext!, [MOMENT_TAG_ID], snapshotAst)
        onAnnotationChange(annotation)
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [annotations, createTagSelectionSnapshot, editor, isAuthor, onAnnotationChange, onAnnotationsBulkUpdate])

  useEffect(() => {
    if (!isAuthor || readOnly || !bookId || !chapterId) {
      return
    }
    const preventEmbedListConversion = (): boolean => {
      if ($selectionContainsEmbed($getSelection())) {
        toast.error('יש לבחור טקסט לעריכה מחוץ לשיבוץ')
        return true
      }
      return false
    }
    const ordered = editor.registerCommand(
      INSERT_ORDERED_LIST_COMMAND,
      preventEmbedListConversion,
      COMMAND_PRIORITY_HIGH,
    )
    const unordered = editor.registerCommand(
      INSERT_UNORDERED_LIST_COMMAND,
      preventEmbedListConversion,
      COMMAND_PRIORITY_HIGH,
    )
    const copy = editor.registerCommand(
      COPY_EMBED_COMMAND,
      () => {
        const snapshot = createEmbedSelectionSnapshot() || lastEmbedSelectionSnapshotRef.current
        if (!snapshot) {
          toast.error('יש להציב את הסמן בתוך הטקסט לשיבוץ')
          return true
        }
        const copySelection = async () => {
          const start = findAnyById(snapshot.ast, snapshot.startBlockId)
          const end = findAnyById(snapshot.ast, snapshot.endBlockId)
          if (!start || !end) {
            throw new Error('לא נמצא טקסט לשיבוץ')
          }
          const startText = 'type' in start ? getBlockText(start) : getInlineText(start.content)
          const endText = 'type' in end ? getBlockText(end) : getInlineText(end.content)
          const sameBlock = snapshot.startBlockId === snapshot.endBlockId
          const wholeBlock =
            sameBlock && (snapshot.collapsed || (snapshot.startOffset === 0 && snapshot.endOffset === endText.length))
          // Persist freshly typed paragraphs before publishing their stable IDs or range anchors.
          flushPendingSyncToParent()
          await onSave?.(snapshot.ast)
          let source: EmbedReference
          if (wholeBlock && 'type' in start) {
            const kind =
              start.type === 'heading' && snapshot.collapsed
                ? findEnclosingChapterSectionHeadingBlockId(snapshot.ast, start.id) === start.id
                  ? 'section'
                  : 'heading'
                : 'block'
            source = { filename: chapterId, blockId: start.id, kind }
          } else {
            const revision = await apiService.getChapterRevision(bookId, chapterId)
            if (!revision.success || !revision.data?.revision) {
              throw new Error('לא ניתן לשמור את הטווח שנבחר')
            }
            const created = await apiService.createEmbedRange(bookId, {
              filename: chapterId,
              startBlockId: snapshot.startBlockId,
              startOffset: snapshot.collapsed ? 0 : snapshot.startOffset,
              endBlockId: snapshot.endBlockId,
              endOffset: snapshot.collapsed ? endText.length : snapshot.endOffset,
              expectedRevision: revision.data.revision,
              expectedStartText: startText,
              expectedEndText: endText,
            })
            if (!created.success || !created.data) {
              throw new Error(created.message || 'לא ניתן לשמור את הטווח שנבחר')
            }
            source = created.data.source
          }
          if (!editor.getRootElement()?.isConnected) {
            return
          }
          await navigator.clipboard.writeText(serializeEmbedClipboard({ bookId, sources: [source] }))
          toast.success(
            source.kind === 'heading'
              ? 'אזור הכותרת הועתק לשיבוץ'
              : source.kind === 'section'
                ? 'המקטע הועתק לשיבוץ'
                : source.kind === 'range'
                  ? 'הטווח המסומן הועתק לשיבוץ'
                  : 'הפסקה הועתקה לשיבוץ',
          )
        }
        void copySelection().catch((error: { message?: string }) =>
          toast.error(error.message || 'לא ניתן להעתיק לשיבוץ'),
        )
        return true
      },
      COMMAND_PRIORITY_LOW,
    )

    const insert = async (
      text: string,
      destination: { selection: RangeSelection; content: string } | null,
    ): Promise<void> => {
      if (!destination) {
        toast.error('יש להציב את הסמן במקום השיבוץ')
        return
      }
      const payload = parseEmbedClipboard(text)
      if (!payload) {
        toast.error('לוח ההעתקה אינו מכיל הפניה לשיבוץ')
        return
      }
      if (payload.bookId !== bookId) {
        toast.error('אפשר לשבץ טקסט מתוך אותו ספר בלבד')
        return
      }
      const source = payload.sources.length === 1 ? payload.sources[0] : undefined
      let inline = false
      if (source && isInlineEmbedReference(source)) {
        const response = await apiService.resolveEmbed(bookId, source)
        if (!response.success || !response.data) {
          throw new Error('לא ניתן לקרוא את מקור השיבוץ')
        }
        inline = response.data.status === 'resolved' && inlineEmbedContent(response.data.content) !== undefined
      }
      if (!editor.getRootElement()?.isConnected) {
        return
      }
      editor.update(
        () => {
          const { selection: savedSelection, content } = destination
          if (
            $embedDestinationContent() !== content ||
            !$getNodeByKey(savedSelection.anchor.key)?.isAttached() ||
            !$getNodeByKey(savedSelection.focus.key)?.isAttached()
          ) {
            toast.error('הטקסט השתנה בזמן ההדבקה. נסה להדביק שוב.')
            return
          }
          // Clipboard permission and source loading may move focus, but must not
          // reapply stale offsets over text typed while the request was pending.
          $setSelection(savedSelection)
          const currentSelection = $getSelection()
          if (!$isRangeSelection(currentSelection)) {
            return
          }
          if (selectionTouchesProtectedContent(currentSelection)) {
            toast.error('יש להציב את הסמן בתוך פסקה, מחוץ לקישור או לטקסט מוצפן')
            return
          }
          if (inline && source && canInsertInlineEmbed(currentSelection)) {
            const node = $createCustomInlineEmbedNode(source)
            $insertNodes([node])
            node.selectNext(0, 0)
            return
          }
          const nodes = payload.sources.map((reference) => $createCustomEmbedNode(generateBlockId(), reference))
          $insertNodes(nodes)
          const last = nodes[nodes.length - 1]
          if (!last.getNextSibling()) {
            const paragraph = $createCustomParagraphNode(generateBlockId())
            last.insertAfter(paragraph)
            paragraph.selectStart()
          } else {
            last.selectNext()
          }
        },
        { tag: 'history-push' },
      )
    }
    const captureSelection = () =>
      editor.getEditorState().read(() => {
        const current = $getSelection()
        return $isRangeSelection(current) ? { selection: current.clone(), content: $embedDestinationContent() } : null
      })
    const reportPasteError = (error: { message?: string }) => toast.error(error.message || 'לא ניתן להדביק כשיבוץ')
    const paste = editor.registerCommand(
      PASTE_EMBED_COMMAND,
      () => {
        const selection = captureSelection()
        if (!selection) {
          toast.error('יש להציב את הסמן במקום השיבוץ')
          return true
        }
        void navigator.clipboard
          .readText()
          .then((text) => insert(text, selection))
          .catch(reportPasteError)
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
    const nativePaste = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData('text/plain') || ''
        if (!text.startsWith(EMBED_CLIPBOARD_PREFIX)) {
          return false
        }
        event.preventDefault()
        void insert(text, captureSelection()).catch(reportPasteError)
        return true
      },
      COMMAND_PRIORITY_HIGH,
    )
    return () => {
      copy()
      paste()
      nativePaste()
      ordered()
      unordered()
    }
  }, [bookId, chapterId, createEmbedSelectionSnapshot, editor, flushPendingSyncToParent, isAuthor, onSave, readOnly])

  // Register ADD_DEP_BEFORE / ADD_DEP_AFTER handlers.
  // Read the live selection + AST inside the handler so the section the cursor
  // is in can be resolved on the fly, then hand off to the host via prop.
  useEffect(() => {
    if (!isAuthor || !onAddDependency) {
      return
    }
    const handle = (direction: 'before' | 'after'): boolean => {
      let selectedText = ''
      let anchorBlockId: string | null = null
      editor.getEditorState().read(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          selectedText = selection.getTextContent()
          anchorBlockId = findBlockIdFromNode(selection.anchor.getNode())
        }
      })
      let headingBlockId: string | null = null
      if (anchorBlockId) {
        const liveAst = getLiveAstFromEditor()
        headingBlockId = findEnclosingChapterSectionHeadingBlockId(liveAst, anchorBlockId)
      }
      onAddDependency({
        direction,
        currentSectionHeadingBlockId: headingBlockId,
        selectedText,
      })
      return true
    }
    const unregBefore = editor.registerCommand(ADD_DEP_BEFORE_COMMAND, () => handle('before'), COMMAND_PRIORITY_LOW)
    const unregAfter = editor.registerCommand(ADD_DEP_AFTER_COMMAND, () => handle('after'), COMMAND_PRIORITY_LOW)
    return () => {
      unregBefore()
      unregAfter()
    }
  }, [editor, isAuthor, onAddDependency, getLiveAstFromEditor])

  // Expose methods via ref
  React.useImperativeHandle(
    editorRef,
    () => ({
      flushPendingSyncToParent,
      getAST: () => {
        const flushed = flushPendingSyncToParent()
        if (flushed) {
          return flushed
        }
        return getLiveAstFromEditor()
      },
      setAST: (newAST: ContentAST) => {
        setAST(newAST)
        const lexicalState = astToLexical(newAST)
        // PERF: Pass object directly to avoid JSON.stringify overhead
        const editorState = editor.parseEditorState(lexicalState)
        editor.setEditorState(editorState)
      },
      refreshFromAST: () => {
        const lexicalState = astToLexical(ast)
        // PERF: Pass object directly to avoid JSON.stringify overhead
        const editorState = editor.parseEditorState(lexicalState)
        editor.setEditorState(editorState)
      },
      save: () => {
        const flushed = flushPendingSyncToParent()
        onSave?.(flushed ?? getLiveAstFromEditor())
      },
      undo: () => {
        activeUndoRedoManager.setUpdatingSelection(true)
        try {
          const result = activeUndoRedoManager.undo()
          if (result.success && result.navigationOnly && result.selection) {
            // Orientation pass: just move the cursor to the next undo target.
            editor.update(() => restoreAstSelection(result.selection!))
          } else if (result.success && result.ast) {
            setAST(result.ast)
            if (result.editorState) {
              applyEditorStatePreservingSelection(editor, result.editorState as EditorState)
            } else {
              const lexicalState = astToLexical(result.ast)
              const editorState = editor.parseEditorState(lexicalState)
              editor.setEditorState(editorState)
            }
            onChange?.(result.ast)
          }
          return result
        } finally {
          setTimeout(() => {
            activeUndoRedoManager.setUpdatingSelection(false)
          }, 0)
        }
      },
      redo: () => {
        activeUndoRedoManager.setUpdatingSelection(true)
        try {
          const result = activeUndoRedoManager.redo()
          if (result.success && result.navigationOnly && result.selection) {
            // Orientation pass: just move the cursor to the next redo target.
            editor.update(() => restoreAstSelection(result.selection!))
          } else if (result.success && result.ast) {
            setAST(result.ast)
            if (result.editorState) {
              applyEditorStatePreservingSelection(editor, result.editorState as EditorState)
            } else {
              const lexicalState = astToLexical(result.ast)
              const editorState = editor.parseEditorState(lexicalState)
              editor.setEditorState(editorState)
            }
            onChange?.(result.ast)
          }
          return result
        } finally {
          setTimeout(() => {
            activeUndoRedoManager.setUpdatingSelection(false)
          }, 0)
        }
      },
      focus: () => editor.focus(),
      blur: () => editor.blur(),
      validate: async () => {
        // Basic validation - check if AST is valid
        return ast.blocks.length > 0
      },
      getEditorCommands: () => activeEditorCommands,
      getUndoRedoManager: () => activeUndoRedoManager,
      getEditor: () => editor,
      triggerASTChange: (newAST: ContentAST) => {
        setAST(newAST)
        onChange?.(newAST)
        const lexicalState = astToLexical(newAST)
        // PERF: Pass object directly to avoid JSON.stringify overhead
        const editorState = editor.parseEditorState(lexicalState)
        editor.setEditorState(editorState)
      },
      scrollToBlock: (blockId: string, highlightText?: string, highlightOffset?: number) => {
        const container = editorContainerRef.current
        return container ? scrollToPassage(container, blockId, highlightText, highlightOffset) : false
      },
      // Direction control methods
      setBlockDirection: (direction: 'ltr' | 'rtl') => {
        if (activeEditorCommands) {
          activeEditorCommands.setBlockDirection(direction)
          const newAST = activeUndoRedoManager.getCurrentAST()
          const sel = activeUndoRedoManager.getCurrentSelection()
          setAST(newAST)
          onChange?.(newAST)
          const lexicalState = astToLexical(newAST)
          // PERF: Pass object directly to avoid JSON.stringify overhead
          const editorState = editor.parseEditorState(lexicalState)
          editor.setEditorState(editorState)
          setTimeout(() => editor.update(() => restoreAstSelection(sel)), 0)
        }
      },
      toggleBlockDirection: () => {
        if (activeEditorCommands) {
          activeEditorCommands.toggleBlockDirection()
          const newAST = activeUndoRedoManager.getCurrentAST()
          const sel = activeUndoRedoManager.getCurrentSelection()
          setAST(newAST)
          onChange?.(newAST)
          const lexicalState = astToLexical(newAST)
          // PERF: Pass object directly to avoid JSON.stringify overhead
          const editorState = editor.parseEditorState(lexicalState)
          editor.setEditorState(editorState)
          setTimeout(() => editor.update(() => restoreAstSelection(sel)), 0)
        }
      },
      getCurrentBlockDirection: () => {
        const selection = activeUndoRedoManager.getCurrentSelection()
        const currentAST = activeUndoRedoManager.getCurrentAST()
        const currentBlock = currentAST.blocks.find((b) => b.id === selection.blockId)

        if (!currentBlock) {
          return currentAST.meta.dir
        }

        // Return explicit direction or inherited direction
        if (currentBlock.attrs && 'dir' in currentBlock.attrs && currentBlock.attrs.dir) {
          return currentBlock.attrs.dir
        }

        // Get inherited direction
        const blockIndex = currentAST.blocks.findIndex((b) => b.id === selection.blockId)
        for (let i = blockIndex - 1; i >= 0; i--) {
          const prevBlock = currentAST.blocks[i]
          if (prevBlock.attrs && 'dir' in prevBlock.attrs && prevBlock.attrs.dir) {
            return prevBlock.attrs.dir
          }
        }

        return currentAST.meta.dir
      },
    }),
    [
      ast,
      setAST,
      onChange,
      onSave,
      activeEditorCommands,
      activeUndoRedoManager,
      editor,
      flushPendingSyncToParent,
      getLiveAstFromEditor,
    ],
  )

  // Register command handlers for UNDO/REDO to use our AST-based undo system
  useEffect(() => {
    const unregisterUndo = editor.registerCommand(
      UNDO_COMMAND,
      () => {
        // Use setUpdatingSelection to prevent captureStateChange from running
        // during the programmatic editor state update
        activeUndoRedoManager.setUpdatingSelection(true)
        try {
          const result = activeUndoRedoManager.undo()
          if (result.success && result.navigationOnly && result.selection) {
            editor.update(() => restoreAstSelection(result.selection!))
          } else if (result.success && result.ast) {
            setAST(result.ast)
            if (result.editorState) {
              applyEditorStatePreservingSelection(editor, result.editorState as EditorState)
            } else {
              const lexicalState = astToLexical(result.ast)
              const editorState = editor.parseEditorState(lexicalState)
              editor.setEditorState(editorState)
            }
            onChange?.(result.ast)
          }
        } finally {
          setTimeout(() => {
            activeUndoRedoManager.setUpdatingSelection(false)
          }, 0)
        }
        return true // Prevent default Lexical undo behavior
      },
      COMMAND_PRIORITY_HIGH,
    )

    const unregisterRedo = editor.registerCommand(
      REDO_COMMAND,
      () => {
        // Use setUpdatingSelection to prevent captureStateChange from running
        // during the programmatic editor state update
        activeUndoRedoManager.setUpdatingSelection(true)
        try {
          const result = activeUndoRedoManager.redo()
          if (result.success && result.navigationOnly && result.selection) {
            editor.update(() => restoreAstSelection(result.selection!))
          } else if (result.success && result.ast) {
            setAST(result.ast)
            if (result.editorState) {
              applyEditorStatePreservingSelection(editor, result.editorState as EditorState)
            } else {
              const lexicalState = astToLexical(result.ast)
              const editorState = editor.parseEditorState(lexicalState)
              editor.setEditorState(editorState)
            }
            onChange?.(result.ast)
          }
        } finally {
          setTimeout(() => {
            activeUndoRedoManager.setUpdatingSelection(false)
          }, 0)
        }
        return true // Prevent default Lexical redo behavior
      },
      COMMAND_PRIORITY_HIGH,
    )

    return () => {
      unregisterUndo()
      unregisterRedo()
    }
  }, [editor, activeUndoRedoManager, setAST, onChange])

  // Cache the last resolvable selection so context-menu actions still work
  // after focus moves from the editor into the menu.
  useEffect(() => {
    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        lastTagSelectionSnapshotRef.current = createTagSelectionSnapshot(editor.getEditorState())
        const embedSnapshot = createEmbedSelectionSnapshot()
        if (embedSnapshot) {
          lastEmbedSelectionSnapshotRef.current = embedSnapshot
        }

        onSelectionChange?.(null)
        return false // Don't prevent other handlers
      },
      COMMAND_PRIORITY_LOW,
    )

    return () => {
      unregisterSelection()
    }
  }, [createTagSelectionSnapshot, createEmbedSelectionSnapshot, editor, onSelectionChange])

  // Persist the user's text selection across app switches. Browsers (especially mobile)
  // clear the DOM selection when the page loses visibility; without this, a user who
  // selects text, switches apps, and returns loses the selection. We continuously track
  // the latest non-collapsed Lexical selection, and re-apply it when the page becomes
  // visible again (only if the current selection is empty so we don't clobber a fresh one).
  const savedSelectionRef = useRef<{
    anchorKey: string
    anchorOffset: number
    anchorType: 'text' | 'element'
    focusKey: string
    focusOffset: number
    focusType: 'text' | 'element'
  } | null>(null)

  // Whether the editor is the page's focus owner. Only *another element* taking focus
  // clears the claim — focus lost to nothing (Chrome's ⌘F find bar, an app switch, the
  // devtools pane) leaves it standing, which is exactly the case worth restoring from.
  const ownsFocusRef = useRef(false)

  useEffect(() => {
    const handleFocusIn = () => {
      const rootElement = editor.getRootElement()
      const active = document.activeElement
      if (!rootElement || !active || active === document.body) {
        return
      }
      ownsFocusRef.current = rootElement.contains(active)
    }
    document.addEventListener('focusin', handleFocusIn)
    return () => {
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [editor])

  useEffect(() => {
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const sel = $getSelection()
        if ($isRangeSelection(sel) && !sel.isCollapsed()) {
          savedSelectionRef.current = {
            anchorKey: sel.anchor.key,
            anchorOffset: sel.anchor.offset,
            anchorType: sel.anchor.type,
            focusKey: sel.focus.key,
            focusOffset: sel.focus.offset,
            focusType: sel.focus.type,
          }
        } else if (document.visibilityState === 'visible' && document.hasFocus()) {
          // The selection collapsed while the page is genuinely active — the user tapped to
          // deselect or placed the cursor. Forget the saved range so it isn't resurrected on
          // the next focus. We intentionally do NOT clear when the page is hidden/unfocused:
          // browsers (mobile especially) collapse the DOM selection on blur, and preserving the
          // saved range across that is the whole point of the restore-on-return behavior.
          savedSelectionRef.current = null
        }
        return false
      },
      COMMAND_PRIORITY_LOW,
    )
    return unregister
  }, [editor])

  useEffect(() => {
    const restoreIfNeeded = () => {
      const saved = savedSelectionRef.current
      if (!saved) {
        return
      }
      // If the user already has a fresh non-collapsed selection on return, don't clobber it.
      let alreadySelected = false
      editor.getEditorState().read(() => {
        const sel = $getSelection()
        if ($isRangeSelection(sel) && !sel.isCollapsed()) {
          alreadySelected = true
        }
      })
      if (alreadySelected) {
        return
      }
      editor.update(() => {
        try {
          const sel = $createRangeSelection()
          sel.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType)
          sel.focus.set(saved.focusKey, saved.focusOffset, saved.focusType)
          $setSelection(sel)
        } catch {
          // Saved selection points to a node that no longer exists — drop it silently.
          savedSelectionRef.current = null
        }
      })
    }

    // Take DOM focus back when the window returns. A surface outside the page —
    // Chrome's ⌘F find bar above all — takes focus away from the contenteditable,
    // and the browser doesn't reliably hand it back: the caret is simply gone and
    // the author has to click into the text before they can keep typing.
    const refocusIfNeeded = () => {
      const rootElement = editor.getRootElement()
      if (!rootElement || readOnly || !ownsFocusRef.current) {
        return
      }
      // Something else on the page claimed focus meanwhile (sidebar, dialog) — leave it.
      const active = document.activeElement
      if (active && active !== document.body && !rootElement.contains(active)) {
        return
      }
      const domSelection = window.getSelection()
      const selectionInsideEditor = Boolean(
        domSelection && domSelection.rangeCount > 0 && rootElement.contains(domSelection.anchorNode),
      )
      // Already focused with a live selection: there is no missing caret to fix, and
      // re-focusing would only scroll the view to a caret the author scrolled away from.
      if (active === rootElement && selectionInsideEditor) {
        return
      }
      // Only restore a caret we actually know the position of. With no selection in the
      // editor state, editor.focus() falls back to selectEnd() — a jump to the end of the
      // document, which is far worse than the missing caret.
      let hasSelection = false
      editor.getEditorState().read(() => {
        hasSelection = $isRangeSelection($getSelection())
      })
      if (!hasSelection) {
        return
      }
      // preventScroll for the same reason as the mount-time auto-focus: the shell locks
      // body scroll, so a focus-driven nudge can't be scrolled back.
      rootElement.focus({ preventScroll: true })
      // Put the caret back in the DOM, but leave the view where it is. `editor.focus()`
      // marks the selection dirty *and* lets Lexical scroll it into view — and after a ⌘F
      // the view is on the match the author went looking for, so that scroll throws the
      // search away before they can even click on it. Nothing is lost by staying put: a
      // click places its own caret, and typing at the restored one scrolls to it anyway.
      editor.update(
        () => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.dirty = true
          }
        },
        { tag: 'skip-scroll-into-view' },
      )
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        restoreIfNeeded()
        refocusIfNeeded()
      }
    }
    const handleFocus = () => {
      restoreIfNeeded()
      refocusIfNeeded()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
    }
  }, [editor, readOnly])

  // Toolbar element (memoized to avoid recreation)
  const toolbarElement = showToolbar ? (
    <LexicalToolbar
      editor={editor}
      editorCommands={activeEditorCommands}
      undoRedoManager={activeUndoRedoManager}
      onSave={handleSave}
      saving={saving}
      autoSaving={autoSaving}
      hasUnsavedChanges={hasUnsavedChanges}
      recentlySaved={recentlySaved}
      showSaveButton={showSaveButton}
      currentDirection={currentDirection}
      onToggleDirection={onToggleDirection}
      onSetDirection={onSetDirection}
      debugMode={debugMode}
      onToggleDebug={onToggleDebug}
      showLineNumbers={showLineNumbers}
      onToggleLineNumbers={onToggleLineNumbers}
      showWritingSyntax={showWritingSyntax}
      onToggleWritingSyntax={
        isAuthor && !readOnly
          ? () => {
              const next = !showWritingSyntax
              localStorage.setItem('editor:show-writing-syntax', String(next))
              setShowWritingSyntax(next)
            }
          : undefined
      }
      onCopyDebugData={handleCopyDebugData}
      readOnly={readOnly}
      bookId={bookId}
      chapterId={chapterId}
      onUndo={() => editorRef.current?.undo()}
      onRedo={() => editorRef.current?.redo()}
      onASTChange={(newAST: ContentAST) => {
        setAST(newAST)
        onChange?.(newAST)
        const lexicalState = astToLexical(newAST)
        // PERF: Pass object directly to avoid JSON.stringify overhead
        const editorState = editor.parseEditorState(lexicalState)
        editor.setEditorState(editorState)
      }}
    />
  ) : null

  return (
    <>
      {/* Toolbar - render via portal if container is available, otherwise render inline */}
      {toolbarElement &&
        (toolbarContainerRef
          ? portalContainerEl && portalContainerEl.isConnected && createPortal(toolbarElement, portalContainerEl)
          : toolbarElement)}

      {/* Main content area */}
      <Box
        ref={editorContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: debugMode ? 'row' : 'column',
          overflow: debugMode ? 'auto' : 'visible',
          minHeight: '160px',
        }}
      >
        {/* Editor */}
        <Box
          sx={{
            position: 'relative',
            flex: 1, // Always fill available space
            display: 'flex', // Enable flex for children
            flexDirection: 'column', // Stack children vertically
            minWidth: debugMode ? '50%' : 'auto',
            paddingLeft: debugMode ? '16px' : { xs: readerView ? '12px' : '24px', sm: '60px', md: '140px' },
            paddingRight: debugMode ? '16px' : { xs: readerView ? '12px' : '24px', sm: '60px', md: '140px' },
            paddingTop: { xs: '16px', md: '32px' },
            paddingBottom: { xs: '16px', md: '32px' },
            ...(readerView ? {} : { background: PAPER_BACKGROUND, '&::after': PAPER_TEXTURE_OVERLAY }),
            cursor: readOnly ? 'default' : 'text', // Show text cursor over entire paper area
          }}
          onClick={(e) => {
            // Focus the editor when clicking on the paper background (not on content)
            // Only call focus() if the click is directly on this Box, not on its children
            // This prevents interfering with native selection handling when clicking on text.
            // Skipped on narrow viewports — the tiny mobile margins are too easy to hit by
            // accident, and editor.focus() would yank the viewport to the existing selection.
            if (!readOnly && !isNarrowViewport && e.target === e.currentTarget) {
              editor.focus()
            }
          }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-editor-content"
                dir={ast.meta.dir}
                style={{
                  padding: '0',
                  outline: 'none',
                  fontFamily: 'inherit',
                  fontSize: readerView ? '17px' : '16px',
                  lineHeight: readerView ? '1.75' : '1.2',
                  letterSpacing: readerView ? '-0.03em' : undefined,
                  direction: ast.meta.dir,
                  flex: 1,
                  minHeight: 0,
                }}
              />
            }
            placeholder={
              <div
                style={{
                  color: 'var(--text-tertiary)',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                }}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {isAuthor && !readOnly && proposalReview && <InlineProposalsPlugin ast={ast} review={proposalReview} />}
          <ListPlugin />
          <ListNodeReplacementPlugin />
          <NodeReplacementPlugin />
          {isAuthor && !readOnly && <WritingSyntaxPlugin annotations={annotations} showSyntax={showWritingSyntax} />}
          <LinkPlugin />
          <ListEditingPlugin />
          <TabIndentationPlugin />
          <KeyboardShortcutsPlugin onSave={handleSave} />
          <SmoothCursorScrollPlugin />
          {isAuthor && !readOnly && <InlineEmbedNavigationPlugin />}
          <ClipboardPlugin defaultLang={ast.meta.lang} defaultDir={ast.meta.dir} bookId={bookId} />
          <DirectionPlugin defaultDirection={ast.meta.dir} autoDetect={true} />
          <EncryptedContentPlugin
            onEncryptedClick={onEncryptedClick}
            onEncryptedContextMenu={onEncryptedContextMenu}
            mnemonicMap={mnemonicMap}
            glyphMap={glyphMap}
            sealedIds={sealedIds}
            onCharacterConsent={onCharacterConsent}
            isAuthor={isAuthor}
            readOnly={readOnly}
          />
          <TagIndicatorPlugin
            annotations={annotations}
            ast={ast}
            isAuthor={isAuthor}
            direction={ast.meta.dir}
            tagRegistry={tagRegistry}
            onBlockHover={handleBlockHover}
          />
          <InlineAnnotationPlugin
            annotations={annotations}
            ast={ast}
            isAuthor={isAuthor}
            tagRegistry={tagRegistry}
            onInlineHover={handleInlineHover}
          />
          <SelectionContextMenuPlugin isAuthor={isAuthor} enableOnMobile={true} annotations={annotations} ast={ast} />

          {/* Tag Overlay — desktop: chip list in side margin. Mobile: dot in the 24px gutter
              (the chip would overlap text), tap-to-expand into a popover. */}
          {isAuthor &&
            hoveredBlockId &&
            tagOverlayPosition &&
            hoveredBlockTagItems.length > 0 &&
            (isNarrowViewport ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: tagOverlayPosition.top + 6,
                  ...(ast.meta.dir === 'rtl' ? { right: 6 } : { left: 6 }),
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: resolveTagColor(hoveredBlockTagItems[0].tagId, tagColorMap, tagRegistry),
                  opacity: 0.9,
                  cursor: 'pointer',
                  zIndex: 1000,
                  // Enlarge tap target without affecting layout
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -8,
                  },
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  clearBlockOverlayTimeout()
                  setBlockPopoverAnchor(event.currentTarget as HTMLElement)
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: tagOverlayPosition.top,
                  ...(tagOverlayPosition.left !== null ? { left: tagOverlayPosition.left } : {}),
                  ...(tagOverlayPosition.right !== null ? { right: tagOverlayPosition.right } : {}),
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: ast.meta.dir === 'rtl' ? 'row-reverse' : 'row',
                  flexWrap: 'wrap',
                  justifyContent: ast.meta.dir === 'rtl' ? 'flex-end' : 'flex-start',
                  gap: '3px',
                  maxWidth: '110px', // Keep tags in margin area with space from side border
                  marginLeft: ast.meta.dir === 'rtl' ? '16px' : 0, // Space from side border (RTL)
                  marginRight: ast.meta.dir === 'ltr' ? '16px' : 0, // Space from side border (LTR)
                  pointerEvents: 'auto',
                }}
                onMouseEnter={clearBlockOverlayTimeout}
                onMouseLeave={scheduleBlockOverlayClear}
              >
                {hoveredBlockTagItems.map(renderTagChip)}
              </Box>
            ))}
          {isNarrowViewport && (
            <Popover
              open={Boolean(blockPopoverAnchor) && hoveredBlockTagItems.length > 0}
              anchorEl={blockPopoverAnchor}
              onClose={() => {
                setBlockPopoverAnchor(null)
                setHoveredBlockId(null)
                setTagOverlayPosition(null)
              }}
              anchorOrigin={{ vertical: 'center', horizontal: ast.meta.dir === 'rtl' ? 'left' : 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: ast.meta.dir === 'rtl' ? 'right' : 'left' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '4px',
                  p: 0.75,
                  maxWidth: 280,
                }}
              >
                {hoveredBlockTagItems.map(renderTagChip)}
              </Box>
            </Popover>
          )}

          {/* Inline Annotation Tag Overlay — same desktop/mobile split as the block overlay above. */}
          {isAuthor &&
            inlineHoverInfo &&
            hoveredInlineTagItems.length > 0 &&
            (isNarrowViewport ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: inlineHoverInfo.position.top + 6,
                  ...(ast.meta.dir === 'rtl' ? { right: 6 } : { left: 6 }),
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: resolveTagColor(hoveredInlineTagItems[0].tagId, tagColorMap, tagRegistry),
                  opacity: 0.9,
                  cursor: 'pointer',
                  zIndex: 1000,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -8,
                  },
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  clearInlineOverlayTimeout()
                  setInlinePopoverAnchor(event.currentTarget as HTMLElement)
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: inlineHoverInfo.position.top,
                  ...(inlineHoverInfo.position.left !== null ? { left: inlineHoverInfo.position.left } : {}),
                  ...(inlineHoverInfo.position.right !== null ? { right: inlineHoverInfo.position.right } : {}),
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: ast.meta.dir === 'rtl' ? 'row-reverse' : 'row',
                  flexWrap: 'wrap',
                  justifyContent: ast.meta.dir === 'rtl' ? 'flex-end' : 'flex-start',
                  gap: '3px',
                  maxWidth: '110px',
                  marginLeft: ast.meta.dir === 'rtl' ? '16px' : 0,
                  marginRight: ast.meta.dir === 'ltr' ? '16px' : 0,
                  pointerEvents: 'auto',
                }}
                onMouseEnter={clearInlineOverlayTimeout}
                onMouseLeave={scheduleInlineOverlayClear}
              >
                {hoveredInlineTagItems.map(renderTagChip)}
              </Box>
            ))}
          {isNarrowViewport && (
            <Popover
              open={Boolean(inlinePopoverAnchor) && hoveredInlineTagItems.length > 0}
              anchorEl={inlinePopoverAnchor}
              onClose={() => {
                setInlinePopoverAnchor(null)
                setInlineHoverInfo(null)
              }}
              anchorOrigin={{ vertical: 'center', horizontal: ast.meta.dir === 'rtl' ? 'left' : 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: ast.meta.dir === 'rtl' ? 'right' : 'left' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '4px',
                  p: 0.75,
                  maxWidth: 280,
                }}
              >
                {hoveredInlineTagItems.map(renderTagChip)}
              </Box>
            </Popover>
          )}
        </Box>

        {/* Debug Panel */}
        {debugMode && <DebugPanel ast={ast} />}
      </Box>

      {bookId && (
        <TagManagerDialog
          open={tagManagerSelectionOpen}
          onClose={handleCloseTagManagerSelection}
          bookId={bookId}
          direction={ast.meta.dir}
          mode="select"
          selectionMode="multiple"
          onSelectManyAndSave={handleSelectTagsFromManager}
          onRegistrySaved={onTagRegistrySaved}
        />
      )}
    </>
  )
}

/**
 * Apply an EditorState from undo/redo, preserving the cursor position when
 * the restored state has no selection (e.g. the mount-time snapshot).
 *
 * When the target state already has a selection, use it directly.
 * When it doesn't (mount-time snapshot), graft the current selection onto
 * the target state via EditorState.clone(selection) — a single atomic
 * operation that avoids the timing issues of setEditorState + editor.update.
 */
function applyEditorStatePreservingSelection(editor: LexicalEditorType, state: EditorState): void {
  const hasSelection = state.read(() => $getSelection() !== null)
  if (hasSelection) {
    editor.setEditorState(state)
    return
  }

  // Menu actions may record a null selection. A newly detached text node does
  // not exist in the pre-detach snapshot, so never graft unchecked node keys.
  const current = editor.getEditorState().read(() => {
    const sel = $getSelection()
    if (!$isRangeSelection(sel)) {
      return null
    }
    const candidates = (point: PointType) => {
      const positions = [{ key: point.key, offset: point.offset, type: point.type }]
      let node = point.getNode()
      let after = point.offset > 0
      let parent = node.getParent()
      while (parent) {
        const offset = node.getIndexWithinParent() + (after ? 1 : 0)
        positions.push({ key: parent.getKey(), offset, type: 'element' })
        after = offset > 0
        node = parent
        parent = node.getParent()
      }
      return positions
    }
    return { selection: sel.clone(), anchor: candidates(sel.anchor), focus: candidates(sel.focus) }
  })
  const selection = state.read(() => {
    if (!current) {
      return null
    }
    const restore = (point: PointType, candidates: typeof current.anchor) => {
      for (const candidate of candidates) {
        const node = $getNodeByKey(candidate.key)
        if (candidate.type === 'text' && $isTextNode(node)) {
          point.set(candidate.key, Math.min(candidate.offset, node.getTextContentSize()), 'text')
          return true
        }
        if (candidate.type === 'element' && $isElementNode(node)) {
          point.set(candidate.key, Math.min(candidate.offset, node.getChildrenSize()), 'element')
          return true
        }
      }
      return false
    }
    return restore(current.selection.anchor, current.anchor) && restore(current.selection.focus, current.focus)
      ? current.selection
      : null
  })

  editor.setEditorState(state.clone(selection))
}

/**
 * Helper to find block ID from a Lexical node
 */
function findBlockIdFromNode(node: any): string | null {
  if (!node) {
    return null
  }

  // Check if node has blockId
  if (node.__blockId) {
    return node.__blockId
  }

  // Check if node has getBlockId method
  if (typeof node.getBlockId === 'function') {
    return node.getBlockId()
  }

  // Try parent
  const parent = node.getParent?.()
  if (parent) {
    return findBlockIdFromNode(parent)
  }

  return null
}

/**
 * Compute block-level text offset from a Lexical text node and its node-relative offset.
 * Lexical's anchor.offset is within the specific text node, not from the block start.
 * We sum preceding text node sizes to get the true block-level offset.
 */
function getBlockLevelOffset(anchorNode: any, anchorOffset: number): number {
  // Walk up to find the block-level parent
  let blockNode = anchorNode
  while (blockNode) {
    if (blockNode.__blockId) {
      break
    }
    blockNode = blockNode.getParent?.()
  }
  if (!blockNode || typeof blockNode.getAllTextNodes !== 'function') {
    return anchorOffset
  }

  const textNodes = blockNode.getAllTextNodes()
  let offset = 0
  for (const textNode of textNodes) {
    if (textNode.getKey() === anchorNode.getKey()) {
      return offset + anchorOffset
    }
    offset += textNode.getTextContentSize()
  }
  return anchorOffset // fallback
}

function clampOffset(offset: number, max: number): number {
  return Math.max(0, Math.min(offset, max))
}

function normalizeWholeSelectionText(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function getSelectionTargetText(target: Block | ListItem): string {
  if ('type' in target) {
    return getBlockText(target)
  }

  let text = getInlineText(target.content)
  if (target.children && target.children.length > 0) {
    const childText = target.children.map((child) => getSelectionTargetText(child)).join('\n')
    text += '\n' + childText
  }
  return text
}

function isListItemTarget(target: Block | ListItem | null): target is ListItem {
  return target != null && !('type' in target)
}

function collectListItemIdsInOrder(ast: ContentAST): string[] {
  const ids: string[] = []

  const visitItems = (items: ListItem[]) => {
    items.forEach((item) => {
      ids.push(item.id)
      if (item.children && item.children.length > 0) {
        visitItems(item.children)
      }
    })
  }

  ast.blocks.forEach((block) => {
    if (block.type === 'list') {
      visitItems(block.items)
    }
  })

  return ids
}

function resolveWholeListItemSelection(
  selection: RangeSelection,
  ast: ContentAST,
  rootElement?: HTMLElement | null,
): string[] | null {
  const nativeBlockIds = getNativeSelectionBlockIds(rootElement ?? null)
  const lexicalAnchorBlockId = findBlockIdFromNode(selection.anchor.getNode())
  const lexicalFocusBlockId = findBlockIdFromNode(selection.focus.getNode())
  const startBlockId = nativeBlockIds?.startBlockId || lexicalAnchorBlockId
  const endBlockId = nativeBlockIds?.endBlockId || lexicalFocusBlockId

  if (!startBlockId || !endBlockId) {
    return null
  }

  const startTarget = findAnyById(ast, startBlockId)
  const endTarget = findAnyById(ast, endBlockId)
  if (!isListItemTarget(startTarget) || !isListItemTarget(endTarget)) {
    return null
  }

  const orderedIds = collectListItemIdsInOrder(ast)
  const startIndex = orderedIds.indexOf(startBlockId)
  const endIndex = orderedIds.indexOf(endBlockId)
  if (startIndex === -1 || endIndex === -1) {
    return null
  }

  const rangeStart = Math.min(startIndex, endIndex)
  const rangeEnd = Math.max(startIndex, endIndex)
  const selectedItemIds = orderedIds.slice(rangeStart, rangeEnd + 1)
  if (selectedItemIds.length === 0) {
    return null
  }

  const selectedText = window.getSelection()?.toString() || selection.getTextContent()
  const selectedItemsText = selectedItemIds
    .map((itemId) => findAnyById(ast, itemId))
    .filter(isListItemTarget)
    .map((item) => getSelectionTargetText(item))
    .join('\n')

  if (
    normalizeWholeSelectionText(selectedText) === '' ||
    normalizeWholeSelectionText(selectedText) !== normalizeWholeSelectionText(selectedItemsText)
  ) {
    return null
  }

  return selectedItemIds
}

function getNativeSelectionWithinBlock(
  rootElement: HTMLElement | null,
  blockId: string,
): { start: number; end: number; text: string } | null {
  if (!rootElement) {
    return null
  }

  const nativeSelection = window.getSelection()
  if (!nativeSelection || nativeSelection.rangeCount === 0) {
    return null
  }

  const nativeRange = nativeSelection.getRangeAt(0)
  const blockElement = rootElement.querySelector(`[data-block-id="${blockId}"]`)
  if (!blockElement) {
    return null
  }

  if (!blockElement.contains(nativeRange.startContainer) || !blockElement.contains(nativeRange.endContainer)) {
    return null
  }

  const measureToPoint = (container: Node, offset: number) => {
    const probe = document.createRange()
    probe.selectNodeContents(blockElement)
    probe.setEnd(container, offset)
    return probe.toString().length
  }

  return {
    start: measureToPoint(nativeRange.startContainer, nativeRange.startOffset),
    end: measureToPoint(nativeRange.endContainer, nativeRange.endOffset),
    text: nativeRange.toString(),
  }
}

function getNativeSelectionBlockIds(
  rootElement: HTMLElement | null,
): { startBlockId: string | null; endBlockId: string | null } | null {
  if (!rootElement) {
    return null
  }

  const nativeSelection = window.getSelection()
  if (!nativeSelection || nativeSelection.rangeCount === 0) {
    return null
  }

  const nativeRange = nativeSelection.getRangeAt(0)
  const resolveBlockIdFromNode = (node: Node | null) => {
    if (!node) {
      return null
    }

    const element = node instanceof Element ? node : node.parentElement
    const blockElement = element?.closest('[data-block-id]')
    if (!blockElement || !rootElement.contains(blockElement)) {
      return null
    }

    return blockElement.getAttribute('data-block-id')
  }

  const resolveBlockIdFromPoint = (node: Node | null, offset: number, bias: 'start' | 'end') => {
    if (!node) {
      return null
    }

    if (!(node instanceof Element)) {
      return resolveBlockIdFromNode(node)
    }

    const childCount = node.childNodes.length
    const candidateIndexes =
      bias === 'end'
        ? [Math.min(offset - 1, childCount - 1), Math.min(offset, childCount - 1)]
        : [Math.min(offset, childCount - 1), Math.max(offset - 1, 0)]

    for (const index of candidateIndexes) {
      if (index < 0 || index >= childCount) {
        continue
      }

      const childNode = node.childNodes[index]
      const childResolved = resolveBlockIdFromNode(childNode)
      if (childResolved) {
        return childResolved
      }
    }

    return resolveBlockIdFromNode(node)
  }

  return {
    startBlockId: resolveBlockIdFromPoint(nativeRange.startContainer, nativeRange.startOffset, 'start'),
    endBlockId: resolveBlockIdFromPoint(nativeRange.endContainer, nativeRange.endOffset, 'end'),
  }
}

function createSelectionContextFromLexicalSelection(
  selection: RangeSelection,
  ast: ContentAST,
  rootElement?: HTMLElement | null,
): SelectionContext | null {
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  const anchorBlockId = findBlockIdFromNode(anchorNode)
  const focusBlockId = findBlockIdFromNode(focusNode)

  if (!anchorBlockId || !focusBlockId) {
    return null
  }

  if (selection.isCollapsed()) {
    return {
      type: 'collapsed',
      startBlockId: anchorBlockId,
      endBlockId: anchorBlockId,
    }
  }

  const isBackward = selection.isBackward()
  const startPoint = isBackward ? selection.focus : selection.anchor
  const endPoint = isBackward ? selection.anchor : selection.focus
  const startNode = startPoint.getNode()
  const endNode = endPoint.getNode()

  const nativeBlockIds = getNativeSelectionBlockIds(rootElement ?? null)
  let startBlockId = nativeBlockIds?.startBlockId || findBlockIdFromNode(startNode)
  let endBlockId = nativeBlockIds?.endBlockId || findBlockIdFromNode(endNode)
  if (!startBlockId || !endBlockId) {
    return null
  }

  let startOffset = getBlockLevelOffset(startNode, startPoint.offset)
  let endOffset = getBlockLevelOffset(endNode, endPoint.offset)
  let selectedText = selection.getTextContent()

  const startTarget = findAnyById(ast, startBlockId)
  const endTarget = findAnyById(ast, endBlockId)
  if (!startTarget || !endTarget) {
    return null
  }

  startOffset = clampOffset(startOffset, getSelectionTargetText(startTarget).length)
  endOffset = clampOffset(endOffset, getSelectionTargetText(endTarget).length)

  if (startBlockId === endBlockId) {
    const blockText = getSelectionTargetText(startTarget)
    const blockLength = blockText.length
    const nativeSelection = getNativeSelectionWithinBlock(rootElement ?? null, startBlockId)
    if (nativeSelection?.text) {
      selectedText = nativeSelection.text
    }
    const rawStart = nativeSelection ? nativeSelection.start : startOffset
    const rawEnd = nativeSelection ? nativeSelection.end : endOffset
    const normalizedStart = Math.min(rawStart, rawEnd)
    const offsetDerivedEnd = Math.max(rawStart, rawEnd)
    const textDerivedEnd =
      selectedText.length > 0 ? clampOffset(normalizedStart + selectedText.length, blockLength) : offsetDerivedEnd
    const normalizedEnd = Math.max(offsetDerivedEnd, textDerivedEnd)
    const selectedWholeTargetByText =
      normalizeWholeSelectionText(selectedText) !== '' &&
      normalizeWholeSelectionText(selectedText) === normalizeWholeSelectionText(blockText)

    if ((normalizedStart === 0 && normalizedEnd === blockLength) || selectedWholeTargetByText) {
      return {
        type: 'singleBlock',
        startBlockId,
        endBlockId,
      }
    }

    return {
      type: 'textRange',
      startBlockId,
      endBlockId,
      startOffset: normalizedStart,
      endOffset: normalizedEnd,
      selectedText,
    }
  }

  const startIndex = ast.blocks.findIndex((block) => block.id === startBlockId)
  const endIndex = ast.blocks.findIndex((block) => block.id === endBlockId)
  if (startIndex === -1 || endIndex === -1) {
    return null
  }

  if (startIndex > endIndex) {
    ;[startBlockId, endBlockId] = [endBlockId, startBlockId]
    ;[startOffset, endOffset] = [endOffset, startOffset]
  }

  const orderedStartBlock = ast.blocks.find((block) => block.id === startBlockId)
  const orderedEndBlock = ast.blocks.find((block) => block.id === endBlockId)
  if (!orderedStartBlock || !orderedEndBlock) {
    return null
  }

  const orderedStartLength = getBlockText(orderedStartBlock).length
  const orderedEndLength = getBlockText(orderedEndBlock).length
  const normalizedStartOffset = clampOffset(startOffset, orderedStartLength)
  const normalizedEndOffset = clampOffset(endOffset, orderedEndLength)

  const coversWholeSpan = normalizedStartOffset === 0 && normalizedEndOffset === orderedEndLength

  if (coversWholeSpan) {
    return {
      type: 'multiBlock',
      startBlockId,
      endBlockId,
    }
  }

  return {
    type: 'textRange',
    startBlockId,
    endBlockId,
    startOffset: normalizedStartOffset,
    endOffset: normalizedEndOffset,
    selectedText,
  }
}

/**
 * Find a Lexical element node by its AST block ID (recursive tree walk)
 */
function findNodeByBlockId(node: any, blockId: string): any {
  if (node.__blockId === blockId) {
    return node
  }
  if (typeof node.getChildren === 'function') {
    for (const child of node.getChildren()) {
      const found = findNodeByBlockId(child, blockId)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Restore Lexical selection from an AST selection.
 * Must be called inside editor.update().
 */
/** Returns false when the block is gone, so callers can fall back to a caret of their own. */
function restoreAstSelection(selection: AstSelection): boolean {
  const root = $getRoot()
  const blockNode = findNodeByBlockId(root, selection.blockId)
  if (!blockNode) {
    return false
  }

  // Walk text nodes to find the correct offset position
  const textNodes = typeof blockNode.getAllTextNodes === 'function' ? blockNode.getAllTextNodes() : []
  let remaining = selection.offset
  for (const textNode of textNodes) {
    const len = textNode.getTextContentSize()
    if (remaining <= len) {
      textNode.select(remaining, remaining)
      return true
    }
    remaining -= len
  }

  // Fallback: select end of block
  if (typeof blockNode.selectEnd === 'function') {
    blockNode.selectEnd()
    return true
  }
  return false
}

/**
 * Main Lexical Editor component with AST backend
 */
export const LexicalEditor = React.forwardRef<LexicalEditorRef, LexicalEditorProps>(
  (
    {
      initialContent,
      proposalReview,
      onChange,
      onSelectionChange,
      placeholder = 'Start writing...',
      readOnly = false,
      readerView = false,
      className,
      style,
      showToolbar = false,
      editorCommands,
      undoRedoManager,
      onSave,
      saving,
      autoSaving,
      hasUnsavedChanges,
      recentlySaved,
      showSaveButton = true,
      currentDirection = 'ltr',
      onToggleDirection,
      onSetDirection,
      debugMode = false,
      onToggleDebug,
      showLineNumbers = false,
      onToggleLineNumbers,
      onEncryptedClick,
      onEncryptedContextMenu,
      mnemonicMap = {},
      glyphMap = {},
      sealedIds,
      onCharacterConsent,
      isAuthor = false,
      bookId,
      chapterId,
      onOpenEmbedSource,
      annotations,
      onAnnotationChange,
      onAnnotationsBulkUpdate,
      onAskAssistant,
      onAnnotationsRepaired,
      toolbarContainerRef,
      bookSettings,
      tagColorMap,
      tagRegistry,
      onTagRegistrySaved,
      initialScrollBlockId,
      initialScrollHighlightText,
      initialScrollHighlightOffset,
      initialScrollTop,
      initialSelection,
      onAddDependency,
    },
    ref,
  ) => {
    // Initialize AST state
    // `initialContent` wins over the manager's copy: a manager handed in from a
    // cache describes whatever it was last edited on, and the content mounted
    // here is the authority on what the author is looking at.
    const [ast, setAST] = useState<ContentAST>(
      () => initialContent || undoRedoManager?.getCurrentAST() || createEmptyAST('mixed', 'ltr'),
    )

    // Initialize undo/redo manager
    const [internalUndoRedoManager] = useState(
      () => undoRedoManager || createUndoRedoManager(ast, createSelection(ast.blocks[0]?.id || 'def01', 0)),
    )

    // Initialize editor commands
    const [internalEditorCommands] = useState(
      () => editorCommands || createEditorCommands(undoRedoManager || internalUndoRedoManager),
    )

    const activeUndoRedoManager = undoRedoManager || internalUndoRedoManager
    const activeEditorCommands = editorCommands || internalEditorCommands

    // Ref for the inner editor
    const editorRefInternal = useRef<LexicalEditorRef | null>(null)

    // Expose methods via ref
    React.useImperativeHandle(ref, () => editorRefInternal.current!, [])

    // Initial editor state from AST
    const initialEditorState = useMemo(() => {
      const lexicalState = astToLexical(ast)
      return JSON.stringify(lexicalState)
    }, []) // Only compute once on mount

    // Lexical editor config
    const initialConfig = useMemo(
      () => ({
        namespace: 'LexicalASTEditor',
        nodes: CUSTOM_NODES,
        editable: !readOnly,
        onError: (error: Error) => {
          logger.error('Lexical editor error:', error)
        },
        editorState: initialEditorState,
        theme: {
          root: 'lexical-root',
          paragraph: 'lexical-paragraph',
          heading: {
            h1: 'lexical-h1',
            h2: 'lexical-h2',
            h3: 'lexical-h3',
            h4: 'lexical-h4',
            h5: 'lexical-h5',
            h6: 'lexical-h6',
          },
          list: {
            nested: {
              listitem: 'lexical-nested-listitem',
            },
            ol: 'lexical-ol',
            ul: 'lexical-ul',
            listitem: 'lexical-listitem',
          },
          quote: 'lexical-quote',
          text: {
            bold: 'lexical-bold',
            italic: 'lexical-italic',
            underline: 'lexical-underline',
            strikethrough: 'lexical-strikethrough',
            code: 'lexical-code',
          },
          link: 'lexical-link',
        },
      }),
      [readOnly, initialEditorState],
    )

    return (
      <Box
        className={className}
        style={style}
        sx={{
          position: 'relative',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          borderRadius: 0,
          direction: ast.meta.dir,
          // Apply book default font settings
          fontFamily: bookSettings?.defaultFontFamily || 'Huninn',
          fontSize: bookSettings?.defaultFontSize ? `${bookSettings.defaultFontSize}pt` : '12pt',
          // All headings (any level, any mode) use the book's title font.
          '& :is(h1, h2, h3, h4, h5, h6, .lexical-h1, .lexical-h2, .lexical-h3, .lexical-h4, .lexical-h5, .lexical-h6)':
            { fontFamily: bookSettings?.titleFontFamily || 'SBL Hebrew' },
          // Heading styling — in reader mode all headings use a uniform size
          ...(readerView
            ? {
                '& .lexical-h1, & h1, & .lexical-h2, & h2, & .lexical-h3, & h3, & .lexical-h4, & h4, & .lexical-h5, & h5, & .lexical-h6, & h6':
                  {
                    fontSize: { xs: '16pt', md: '15pt' },
                    fontWeight: 500,
                    lineHeight: 1.3,
                    margin: { xs: '1.5em 0 1em 0', md: '1.4em 0 1em 0' },
                    color: 'var(--editor-text)',
                  },
              }
            : {
                '& .lexical-h1, & h1': {
                  fontSize: { xs: '28pt', md: '26pt' },
                  fontWeight: 400,
                  lineHeight: { xs: '1.3', md: '1.15' },
                  margin: { xs: '1.8em 0 0.8em 0', md: '1.6em 0 0.7em 0' },
                  color: 'var(--editor-text)',
                },
                '& .lexical-h2, & h2': {
                  fontSize: { xs: '22pt', md: '20pt' },
                  fontWeight: 400,
                  lineHeight: { xs: '1.3', md: '1.15' },
                  margin: { xs: '1.5em 0 0.7em 0', md: '1.4em 0 0.6em 0' },
                  color: 'var(--editor-text)',
                },
                '& .lexical-h3, & h3': {
                  fontSize: { xs: '18pt', md: '16pt' },
                  fontWeight: 400,
                  lineHeight: { xs: '1.3', md: '1.15' },
                  margin: { xs: '1.3em 0 0.6em 0', md: '1.2em 0 0.5em 0' },
                  color: 'var(--editor-text)',
                },
                '& .lexical-h4, & h4': {
                  fontSize: '14pt',
                  fontWeight: 400,
                  lineHeight: '1.15',
                  margin: '1.1em 0 0.5em 0',
                  color: 'var(--editor-text)',
                },
                '& .lexical-h5, & h5': {
                  fontSize: '12pt',
                  fontWeight: 400,
                  lineHeight: '1.15',
                  margin: '1em 0 0.5em 0',
                  color: 'var(--editor-text)',
                },
                '& .lexical-h6, & h6': {
                  fontSize: '11pt',
                  fontWeight: 400,
                  lineHeight: '1.15',
                  margin: '0.9em 0 0.5em 0',
                  color: 'var(--editor-text)',
                },
              }),
          '& .lexical-paragraph, & p': {
            fontSize: { xs: '14pt', md: '12pt' },
            fontWeight: 400,
            lineHeight: readerView ? { xs: '1.35', md: '1.35' } : { xs: '1.25', md: '1.15' },
            margin: { xs: '0 0 1em 0', md: '0 0 1em 0' },
            color: 'var(--editor-text)',
            minHeight: '1.15em', // Ensure empty paragraphs are clickable
          },
          // Reader-only: a passage that is encrypted to everyone else, and open to the
          // person reading it, carries a hairline in the margin — the printer's change
          // bar. It sits outside the text block, so it says "this part is not public"
          // without touching a letter of what is being read.
          ...(readerView && {
            '& .lexical-paragraph:has(.encrypted-readable), & p:has(.encrypted-readable)': {
              position: 'relative',
            },
            '& .lexical-paragraph:has(.encrypted-readable)::before, & p:has(.encrypted-readable)::before': {
              content: '""',
              position: 'absolute',
              insetBlock: '0.2em',
              // Inline-start, so it follows the book's direction into the outer margin.
              insetInlineStart: { xs: '-0.6em', md: '-1.1em' },
              width: '1px',
              backgroundColor: 'var(--ornament-stroke)',
              opacity: 0.55,
              pointerEvents: 'none',
            },
          }),
          // Reader-only: paragraphs that contain line breaks (<br>) are treated as
          // dialogue blocks — indented from both sides, with space between turns
          // (one per <br>) but normal line-height within a turn (so soft-wrapped
          // text inside a single line doesn't pick up the extra spacing).
          //
          // A break must have something after it to count. Lexical appends a filler
          // <br> to any element whose last child is an inline decorator — so a
          // paragraph that is entirely one encrypted passage ends in a <br> it never
          // asked for, and a bare `:has(br)` indented it as if it were dialogue.
          ...(readerView && {
            '& .lexical-paragraph:has(br:not(:last-child)), & p:has(br:not(:last-child))': {
              marginInline: { xs: '1em', md: '1.5em' },
              marginBlock: { xs: '0.5em 1.5em', md: '0.5em 1.75em' },
            },
            '& .lexical-paragraph:has(br:not(:last-child)) br, & p:has(br:not(:last-child)) br': {
              content: '""',
              display: 'block',
              marginBottom: { xs: '0.6em', md: '0.7em' },
            },
          }),
          '& .lexical-editor-content > *:last-child': {
            marginBottom: 0,
          },
          // Ensure editor content fills available space
          '& .lexical-editor-content': {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          },
          // List styling - following Lexical playground patterns.
          // A list is a block in the same vertical rhythm as a paragraph: space
          // below it, none above (the preceding block supplies that), and nothing
          // between items. Without the bottom margin a list sits flush against the
          // paragraph after it while still being spaced from the one before it.
          '& .lexical-ul, & ul': {
            padding: 0,
            margin: '0 0 1em 0',
            listStyleType: 'disc',
            listStylePosition: 'outside',
          },
          '& .lexical-ol, & ol': {
            padding: 0,
            margin: '0 0 1em 0',
            listStyleType: 'decimal',
            listStylePosition: 'outside',
          },
          // A nested list is part of its parent list, not a block after it.
          // Lexical puts .lexical-ul/.lexical-ol on nested lists too, so this has to
          // out-specify the class rules above — `li > ul` alone loses to `.lexical-ul`.
          '& li > .lexical-ul, & li > .lexical-ol, & li > ul, & li > ol': {
            marginBottom: 0,
          },
          // List items get base margin for indentation
          '& .lexical-listitem, & li': {
            marginInlineStart: '32px',
            marginInlineEnd: '32px',
            color: 'var(--editor-text)',
          },
          '& .lexical-listitem::marker, & li::marker': {
            color: 'var(--editor-text)',
          },
          // Override Lexical's default padding-based indent with margin-based indent
          // This ensures the bullet moves with the text, not just the text
          '& .lexical-listitem[style*="--lexical-indent"], & li[style*="--lexical-indent"]': {
            paddingInlineStart: '0 !important', // Remove Lexical's padding
            marginInlineStart: 'calc(32px + var(--lexical-indent, 0px))', // Use margin instead
          },
          // CRITICAL: nested listitem is the WRAPPER that contains a nested list
          // It should have NO bullet - the actual content is inside the nested ul/ol
          '& .lexical-nested-listitem': {
            listStyleType: 'none',
          },
          '& .lexical-nested-listitem:before, & .lexical-nested-listitem:after': {
            display: 'none',
          },
          // Quote styling
          '& .lexical-quote, & blockquote': {
            margin: '16px 0',
            padding: '8px 16px',
            borderLeft: '4px solid var(--editor-quote-border)',
            fontStyle: 'italic',
            color: 'var(--editor-quote-text)',
          },
          // Text formatting
          '& .lexical-bold, & strong': {
            fontWeight: 'bold',
          },
          '& .lexical-italic, & em': {
            fontStyle: 'italic',
          },
          '& .lexical-underline': {
            textDecoration: 'underline',
          },
          '& .lexical-strikethrough': {
            textDecoration: 'line-through',
          },
          '& .lexical-code': {
            fontFamily: 'monospace',
            backgroundColor: 'var(--editor-code-bg)',
            padding: '2px 4px',
            borderRadius: '3px',
          },
          // Link styling
          '& .lexical-link, & a': {
            color: 'var(--editor-link)',
            textDecoration: 'underline',
          },
          // Direction-specific styling
          '& [dir="rtl"]': {
            direction: 'rtl',
            textAlign: 'right',
          },
          '& [dir="ltr"]': {
            direction: 'ltr',
            textAlign: 'left',
          },
          // Encrypted content styling (editor only — reader shows plain glyphs)
          ...(readerView
            ? {}
            : {
                '& .encrypted-content': {
                  backgroundColor: 'rgba(255, 255, 255, 0)',
                  borderBottom: '1.5px dashed rgba(255, 255, 255, 0.4)',
                },
              }),
        }}
      >
        <EmbedContext.Provider value={{ bookId, isAuthor, onOpenSource: onOpenEmbedSource }}>
          <LexicalComposer initialConfig={initialConfig}>
            <LexicalEditorInner
              ast={ast}
              proposalReview={proposalReview}
              setAST={setAST}
              onChange={onChange}
              onSelectionChange={onSelectionChange}
              placeholder={placeholder}
              readOnly={readOnly}
              readerView={readerView}
              showToolbar={showToolbar}
              activeEditorCommands={activeEditorCommands}
              activeUndoRedoManager={activeUndoRedoManager}
              onSave={onSave}
              saving={saving}
              autoSaving={autoSaving}
              hasUnsavedChanges={hasUnsavedChanges}
              recentlySaved={recentlySaved}
              showSaveButton={showSaveButton}
              currentDirection={currentDirection}
              onToggleDirection={onToggleDirection}
              onSetDirection={onSetDirection}
              debugMode={debugMode}
              onToggleDebug={onToggleDebug}
              showLineNumbers={showLineNumbers}
              onToggleLineNumbers={onToggleLineNumbers}
              onEncryptedClick={onEncryptedClick}
              onEncryptedContextMenu={onEncryptedContextMenu}
              mnemonicMap={mnemonicMap}
              glyphMap={glyphMap}
              sealedIds={sealedIds}
              onCharacterConsent={onCharacterConsent}
              isAuthor={isAuthor}
              bookId={bookId}
              chapterId={chapterId}
              annotations={annotations}
              onAnnotationChange={onAnnotationChange}
              onAnnotationsBulkUpdate={onAnnotationsBulkUpdate}
              onAskAssistant={onAskAssistant}
              onAnnotationsRepaired={onAnnotationsRepaired}
              toolbarContainerRef={toolbarContainerRef}
              editorRef={editorRefInternal}
              bookSettings={bookSettings}
              tagColorMap={tagColorMap}
              tagRegistry={tagRegistry}
              onTagRegistrySaved={onTagRegistrySaved}
              initialScrollBlockId={initialScrollBlockId}
              initialScrollHighlightText={initialScrollHighlightText}
              initialScrollHighlightOffset={initialScrollHighlightOffset}
              initialScrollTop={initialScrollTop}
              initialSelection={initialSelection}
              onAddDependency={onAddDependency}
            />
          </LexicalComposer>
        </EmbedContext.Provider>
      </Box>
    )
  },
)

LexicalEditor.displayName = 'LexicalEditor'

export default LexicalEditor
